import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import * as api from "./api";
import {
  Search, MapPin, Clock, Briefcase, ChevronRight, ChevronLeft, X, Upload,
  FileText, Download, Shield, Plus, Filter, Check, AlertCircle, Loader2,
  Trash2, BarChart3, Users, Award, DollarSign, Plane, Link2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ScatterChart,
  Scatter, ErrorBar, CartesianGrid, Cell, LabelList,
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════
   Brand tokens
   ═══════════════════════════════════════════════════════════════════ */
const BRAND = "#A80600";
const INK = "#2A2A2A";
const NEAR_BLACK = "#151515";
const PAPER = "#F7F5F3";
const RULE = "#E2DDD9";
const MUTED = "#6E6866";

const ADMIN_PASSCODE = "linchpin2026"; // prototipo — en Azure lo reemplaza Entra ID

const FONT = `'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

const DEPARTMENTS = ["Data platforms", "AI & GenAI", "Analytics", "Governance", "Enablement"];
const STATUSES = ["Received", "Under review", "Interview", "Offer", "Hired", "Not selected"];

const STATUS_COLOR = {
  "Received": "#6E6866", "Under review": "#B26B00", "Interview": "#1D4ED8",
  "Offer": "#166534", "Hired": "#0F766E", "Not selected": "#9A9290",
};

/* Candidates type an exact monthly figure in USD — required, no default.
   These bands are two things and neither is the question we ask:
   the buckets the histogram groups those figures into, and the codes we
   budget a role at internally. Ordered light to dark so the ladder reads
   as "junior money → senior money". */
const BANDS = [
  { code: "B1", label: "Under $1,500", short: "<1.5k", min: 0, color: "#E9BBB7" },
  { code: "B2", label: "$1,500 – $2,500", short: "1.5–2.5k", min: 1500, color: "#DC9A94" },
  { code: "B3", label: "$2,500 – $3,500", short: "2.5–3.5k", min: 2500, color: "#CB6E66" },
  { code: "B4", label: "$3,500 – $5,000", short: "3.5–5k", min: 3500, color: "#B8352C" },
  { code: "B5", label: "$5,000 – $7,000", short: "5–7k", min: 5000, color: "#A80600" },
  { code: "B6", label: "$7,000 or more", short: "7k+", min: 7000, color: "#6E0400" },
];
const band = (code) => BANDS.find((b) => b.code === code);
const bucketOf = (usd) => [...BANDS].reverse().find((b) => usd >= b.min) || BANDS[0];

const SALARY_MIN = 100;
const SALARY_MAX = 100000;

/* Country of residence. A catalog, not free text: "CR", "costa rica" and
   "Costa Rica " are three rows in a chart and one country in reality.
   Costa Rica leads because it's where most applicants live — ordering a
   factual question for convenience is fine; nobody is nudged into moving. */
const COUNTRIES = [
  { code: "CR", name: "Costa Rica" }, { code: "AR", name: "Argentina" },
  { code: "BO", name: "Bolivia" }, { code: "BR", name: "Brazil" },
  { code: "CL", name: "Chile" }, { code: "CO", name: "Colombia" },
  { code: "CU", name: "Cuba" }, { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" }, { code: "SV", name: "El Salvador" },
  { code: "GT", name: "Guatemala" }, { code: "HN", name: "Honduras" },
  { code: "MX", name: "Mexico" }, { code: "NI", name: "Nicaragua" },
  { code: "PA", name: "Panama" }, { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" }, { code: "PR", name: "Puerto Rico" },
  { code: "ES", name: "Spain" }, { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" }, { code: "VE", name: "Venezuela" },
  { code: "XX", name: "Elsewhere" },
];
const country = (code) => COUNTRIES.find((c) => c.code === code);

/* Travel. Our clients are banks and ministries across LATAM and some of
   the work only happens in their building. Graded, because "willing to
   travel: yes/no" answers nothing you can staff against. */
const TRAVEL = [
  { code: "NONE", label: "Not able to travel", short: "None", color: "#E9BBB7" },
  { code: "OCCASIONAL", label: "Occasionally — a few trips a year", short: "Occasional", color: "#D4736C" },
  { code: "REGULAR", label: "Regularly — up to about a week a month", short: "Regular", color: "#B8352C" },
  { code: "FREQUENT", label: "Frequently — including multi-week stays at a client site", short: "Frequent", color: "#7C0400" },
];
const travel = (code) => TRAVEL.find((t) => t.code === code);
const TRAVELS = ["REGULAR", "FREQUENT"];

/* Three states, because "is it on the site" and "are we still taking
   applications" are different questions and one flag can't answer both. */
const JOB_STATUS = [
  { code: "draft", label: "Draft", hint: "Not on the site. Work in progress.", color: "#8A8280" },
  { code: "published", label: "Published", hint: "Live on the site, accepting applications.", color: "#166534" },
  { code: "closed", label: "Closed", hint: "Off the site. Keeps its applications and history.", color: "#9A9290" },
];
const jobStatus = (code) => JOB_STATUS.find((j) => j.code === code) || JOB_STATUS[0];

/* Costa Rican / LATAM ladder, labelled so a candidate from anywhere in
   the region knows which rung is theirs. */
const EDU_LEVELS = [
  { code: "NONE", label: "No degree required", short: "No degree" },
  { code: "TECH", label: "Técnico / technical diploma", short: "Técnico" },
  { code: "BACH", label: "Bachillerato universitario (4-year bachelor's)", short: "Bachillerato" },
  { code: "LIC", label: "Licenciatura", short: "Licenciatura" },
  { code: "MAST", label: "Maestría (master's)", short: "Maestría" },
  { code: "PHD", label: "Doctorado", short: "Doctorado" },
];
const eduLevel = (code) => EDU_LEVELS.find((e) => e.code === code) || EDU_LEVELS[0];

const MASTER_REQ = [
  { code: "NONE", label: "Not required" },
  { code: "PREFERRED", label: "Preferred" },
  { code: "REQUIRED", label: "Required" },
];

/* Suggestions, not a closed list — the editor lets you type anything.
   Boxing a recruiter into a catalog of degrees just makes them pick the
   nearest wrong one. */
const FIELDS = [
  "Computer Science", "Software Engineering", "Information Systems", "Industrial Engineering",
  "Electronics Engineering", "Information Security", "Data Science", "Statistics", "Mathematics",
  "Economics", "Actuarial Science", "Accounting", "Business Administration", "Law",
  "Psychology", "Communications", "Education",
];

/* Certifications are a catalog now (careers.certification), loaded at runtime
   and threaded down as `certs`. Jobs and applications store the cert CODE;
   certName resolves a code to its label for display. */
const certName = (catalog, code) => catalog.find((c) => c.code === code)?.name || code;
const certNames = (catalog, codes = []) => codes.map((c) => certName(catalog, c));

/* The storage layer that used to be here is gone: it lives in api.js and
   talks to /api. Everything below this line was written against it and did
   not change. */

const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString();

/* ═══════════════════════════════════════════════════════════════════
   The 25 roles. `band` is the budgeted band — internal, never rendered
   on the public page, but the analytics compares expectations to it.
   ═══════════════════════════════════════════════════════════════════ */
const CR = "San José, Costa Rica";
const LATAM = "Remote — Latin America";
const CO = "Bogotá, Colombia";

/* Structured requirements, kept beside the prose rather than inside it.
   The prose persuades; these are what a recruiter filters and reports on.
   Merged into the seed below so each posting object stays readable. */
const REQS = {
  JR104812: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Information Systems"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["DP-600 Fabric Analytics Engineer", "DP-700 Fabric Data Engineer"] },
  JR104790: { yearsRequired: 8, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Information Systems"], masterReq: "PREFERRED", masterFields: ["Computer Science", "Data Science"], certsRequired: ["DP-600 Fabric Analytics Engineer"], certsPreferred: ["AZ-305 Azure Solutions Architect"] },
  JR104815: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Information Systems"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["Databricks certification"] },
  JR104818: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Information Systems"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["AZ-104 Azure Administrator"] },
  JR104821: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Information Systems", "Industrial Engineering"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
  JR104824: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },

  JR104809: { yearsRequired: 3, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Data Science"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["AI-102 Azure AI Engineer"] },
  JR104827: { yearsRequired: 7, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Data Science"], masterReq: "PREFERRED", masterFields: ["Computer Science", "Data Science"], certsRequired: ["AI-102 Azure AI Engineer"], certsPreferred: ["AZ-305 Azure Solutions Architect"] },
  JR104830: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Computer Science", "Software Engineering", "Data Science", "Statistics"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
  JR104833: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Statistics", "Mathematics", "Economics", "Actuarial Science", "Data Science"], masterReq: "PREFERRED", masterFields: ["Statistics", "Economics", "Data Science"], certsRequired: [], certsPreferred: [] },
  JR104836: { yearsRequired: 3, edu: "BACH", bachelorFields: ["Computer Science", "Statistics", "Data Science"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },

  JR104803: { yearsRequired: 3, edu: "BACH", bachelorFields: ["Information Systems", "Computer Science", "Industrial Engineering", "Business Administration"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["PL-300 Power BI Data Analyst"] },
  JR104839: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Information Systems", "Computer Science", "Industrial Engineering"], masterReq: "NONE", masterFields: [], certsRequired: ["PL-300 Power BI Data Analyst"], certsPreferred: ["DP-600 Fabric Analytics Engineer"] },
  JR104842: { yearsRequired: 3, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Statistics"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
  JR104845: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Accounting", "Economics", "Actuarial Science", "Business Administration"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
  JR104771: { yearsRequired: 1, edu: "BACH", bachelorFields: ["Information Systems", "Computer Science", "Statistics", "Economics", "Industrial Engineering"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["PL-300 Power BI Data Analyst"] },

  JR104797: { yearsRequired: 6, edu: "BACH", bachelorFields: ["Information Systems", "Computer Science", "Business Administration", "Industrial Engineering"], masterReq: "PREFERRED", masterFields: ["Data Science", "Business Administration"], certsRequired: [], certsPreferred: ["CDMP (DAMA)"] },
  JR104848: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Statistics"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
  JR104851: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Information Systems", "Computer Science", "Business Administration", "Law"], masterReq: "PREFERRED", masterFields: [], certsRequired: ["ISO/IEC 42001 Lead Implementer"], certsPreferred: [] },
  JR104854: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Information Security"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["CISSP / CISM"] },
  JR104857: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Industrial Engineering"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["CDMP (DAMA)"] },

  JR104784: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Education"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["Microsoft Certified Trainer", "DP-600 Fabric Analytics Engineer"] },
  JR104860: { yearsRequired: 6, edu: "BACH", bachelorFields: ["Industrial Engineering", "Business Administration", "Computer Science", "Information Systems"], masterReq: "PREFERRED", masterFields: ["Business Administration"], certsRequired: [], certsPreferred: ["PMP or Scrum Master"] },
  JR104863: { yearsRequired: 5, edu: "BACH", bachelorFields: ["Computer Science", "Information Systems", "Industrial Engineering", "Business Administration"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: ["DP-600 Fabric Analytics Engineer"] },
  JR104866: { yearsRequired: 4, edu: "BACH", bachelorFields: ["Psychology", "Communications", "Business Administration", "Education"], masterReq: "NONE", masterFields: [], certsRequired: [], certsPreferred: [] },
};

const RAW_JOBS = [
  /* ── Data platforms ──────────────────────────────────────────── */
  {
    id: "JR104812", title: "Senior Data Engineer — Microsoft Fabric", department: "Data platforms",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(0), status: "published", band: "B4",
    summary: "Build the lakehouses that regulators and banks run on. You own end-to-end pipelines in Microsoft Fabric for clients where an incorrect number is a compliance event.",
    responsibilities: [
      "Design medallion architectures in Fabric Lakehouse and Warehouse.",
      "Build ingestion and transformation with PySpark notebooks and Data Factory pipelines.",
      "Model semantic layers analysts can trust without a translator.",
      "Set up CI/CD with deployment pipelines and Git across dev, QA and prod.",
    ],
    qualifications: [
      "5+ years in data engineering, 2+ on the Microsoft stack.",
      "Strong PySpark and SQL. Delta Lake internals are not a mystery to you.",
      "Experience in a regulated environment.",
      "Professional English and Spanish.",
    ],
    nice: ["DP-600 or DP-700", "Purview exposure"],
  },
  {
    id: "JR104790", title: "Fabric Solution Architect", department: "Data platforms",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(6), status: "published", band: "B5",
    summary: "Own the architecture from the first workshop to the production handover, and the trade-offs in between.",
    responsibilities: [
      "Lead architecture design for Fabric platforms in regulated institutions.",
      "Define capacity, workspace topology, security and networking.",
      "Be the technical counterpart to CIOs and Microsoft account teams.",
      "Mentor engineers and review the decisions that are expensive to reverse.",
    ],
    qualifications: [
      "8+ years in data, 3+ architecting cloud platforms.",
      "Deep Azure: Fabric, Synapse, Databricks, networking, Entra ID.",
      "Platforms that survived their first audit.",
      "Professional English and Spanish.",
    ],
    nice: ["Microsoft MVP or Fabric Super User", "Pre-sales experience"],
  },
  {
    id: "JR104815", title: "Data Engineer — Azure Databricks", department: "Data platforms",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(1), status: "published", band: "B4",
    summary: "Not every client is on Fabric. For the ones on Databricks, you build the same reliability with different tools.",
    responsibilities: [
      "Build Delta Live Tables pipelines and Unity Catalog structures.",
      "Tune Spark jobs that got written for correctness and now need to be fast.",
      "Bridge Databricks and Power BI without losing lineage on the way.",
    ],
    qualifications: [
      "4+ years with Spark in production, 2+ on Databricks.",
      "Unity Catalog, Delta Lake, and a real opinion about cluster sizing.",
      "Professional English and Spanish.",
    ],
    nice: ["Databricks Data Engineer Professional", "Fabric exposure"],
  },
  {
    id: "JR104818", title: "Platform Engineer — DataOps & CI/CD", department: "Data platforms",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(2), status: "published", band: "B4",
    summary: "Make the boring parts automatic. Deployments, environments, tests, secrets — the things that decide whether a platform is maintainable in year two.",
    responsibilities: [
      "Build Terraform and Bicep modules for Fabric, Databricks and Azure data services.",
      "Design branching and release flows across dev, QA and prod workspaces.",
      "Automate data quality gates so a bad load fails the pipeline, not the report.",
      "Instrument cost and capacity so the client is never surprised by the invoice.",
    ],
    qualifications: [
      "4+ years in DevOps or platform engineering, on data systems specifically.",
      "Terraform or Bicep, Azure DevOps or GitHub Actions, and strong scripting.",
      "Professional English and Spanish.",
    ],
    nice: ["AZ-400", "FinOps experience"],
  },
  {
    id: "JR104821", title: "Integration Engineer — Core Banking to Lakehouse", department: "Data platforms",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(3), status: "published", band: "B4",
    summary: "Core banking systems were not built to be read from. You get the data out anyway, without anyone noticing on the transactional side.",
    responsibilities: [
      "Design CDC and batch extraction from core banking, HOST and legacy sources.",
      "Build event ingestion with Event Hubs, Kafka or Service Bus where streaming earns its keep.",
      "Reconcile: prove the lakehouse and the source agree, every day, in writing.",
    ],
    qualifications: [
      "5+ years integrating enterprise systems, with financial core exposure.",
      "CDC tooling, message brokers, and API design.",
      "Professional English and Spanish.",
    ],
    nice: ["COBOL or AS/400 literacy", "ISO 20022 or SWIFT familiarity"],
  },
  {
    id: "JR104824", title: "Database Engineer — SQL Server & PostgreSQL", department: "Data platforms",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(8), status: "published", band: "B3",
    summary: "Migrations that don't lose rows, and databases that stay fast after the consultants leave.",
    responsibilities: [
      "Plan and execute migrations to Azure SQL and PostgreSQL Flexible Server.",
      "Tune queries, indexes and plans where a report takes minutes and shouldn't.",
      "Design HA, backup and restore that has actually been tested.",
    ],
    qualifications: [
      "5+ years as a DBA or database engineer across SQL Server and PostgreSQL.",
      "You have restored a production database under pressure and can talk about it.",
      "Professional English and Spanish.",
    ],
    nice: ["DP-300", "Always On or Patroni experience"],
  },

  /* ── AI & GenAI ──────────────────────────────────────────────── */
  {
    id: "JR104809", title: "AI Engineer — GenAI & RAG", department: "AI & GenAI",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(1), status: "published", band: "B4",
    summary: "Take retrieval-augmented assistants from a promising notebook to something a clinician or an analyst uses on a Tuesday morning without thinking about it.",
    responsibilities: [
      "Design chunking, embedding and retrieval strategies over institutional corpora.",
      "Ship APIs with FastAPI and deploy on Azure App Service or Container Apps.",
      "Instrument evaluation: groundedness, retrieval quality, refusal behaviour, latency.",
      "Turn 'the answer felt wrong' into a measurable defect.",
    ],
    qualifications: [
      "3+ years in software or ML engineering, with production LLM work.",
      "Python, FastAPI, Azure OpenAI and Azure AI Search.",
      "You can explain why a RAG system hallucinated and then prove the fix.",
      "Professional English and Spanish.",
    ],
    nice: ["AI-102", "Healthcare or financial data experience"],
  },
  {
    id: "JR104827", title: "AI Solutions Architect", department: "AI & GenAI",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(2), status: "published", band: "B5",
    summary: "Decide what should and shouldn't be an AI system, in front of the client, with the reasons written down.",
    responsibilities: [
      "Lead architecture for GenAI and ML solutions in banking and healthcare.",
      "Define the evaluation and guardrail strategy before the first model call.",
      "Cost, latency and accuracy: name the trade-off and defend the choice.",
      "Mentor engineers; kill the projects that shouldn't ship.",
    ],
    qualifications: [
      "7+ years in software or data, 2+ architecting AI systems in production.",
      "Azure AI Foundry, Azure OpenAI, AI Search, and orchestration frameworks.",
      "Professional English and Spanish.",
    ],
    nice: ["AI-102 and AZ-305", "Published or spoken on applied AI"],
  },
  {
    id: "JR104830", title: "Machine Learning Engineer — MLOps", department: "AI & GenAI",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(5), status: "published", band: "B4",
    summary: "Models in production, monitored, retrained, and explainable to a regulator who does not care about your F1 score.",
    responsibilities: [
      "Build training and inference pipelines in Azure ML.",
      "Monitor drift and set the retraining trigger, not just the dashboard.",
      "Package models so deployment is a pipeline run, not an event.",
    ],
    qualifications: [
      "4+ years in ML engineering with models actually serving traffic.",
      "Python, MLflow, Azure ML, containers.",
      "Professional English and Spanish.",
    ],
    nice: ["DP-100", "Model risk management exposure"],
  },
  {
    id: "JR104833", title: "Applied Data Scientist — Risk & Fraud", department: "AI & GenAI",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(7), status: "published", band: "B4",
    summary: "Credit risk, AML, fraud. Models that have to be right and have to be explainable, in that order.",
    responsibilities: [
      "Build scoring and anomaly detection models on financial data.",
      "Validate: backtesting, stability, and the fairness question before someone else asks it.",
      "Write the model documentation the regulator will read.",
    ],
    qualifications: [
      "4+ years in data science, with risk, fraud or AML work in a financial institution.",
      "Python, strong statistics, and gradient boosting you can actually interpret.",
      "Professional English and Spanish.",
    ],
    nice: ["Economics or statistics postgraduate", "SUGEF or local regulatory experience"],
  },
  {
    id: "JR104836", title: "AI Evaluation Engineer", department: "AI & GenAI",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(4), status: "published", band: "B3",
    summary: "Our clients cannot ship an assistant on a demo and a good feeling. You build the evidence that it works.",
    responsibilities: [
      "Design evaluation sets with domain experts — clinicians, analysts, auditors.",
      "Build automated eval harnesses: groundedness, retrieval hit rate, refusal, regression.",
      "Report accuracy honestly, including when the answer is 'not yet'.",
    ],
    qualifications: [
      "3+ years in ML, QA engineering or data science.",
      "Python, and enough statistics to know when a difference is noise.",
      "Professional English and Spanish.",
    ],
    nice: ["Experience with LLM eval frameworks", "Human annotation programme design"],
  },

  /* ── Analytics ───────────────────────────────────────────────── */
  {
    id: "JR104803", title: "Analytics Consultant — Power BI", department: "Analytics",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(2), status: "published", band: "B3",
    summary: "Sit with the people who make decisions, find the question behind the request, and build the model that answers it.",
    responsibilities: [
      "Run discovery with business and regulatory stakeholders.",
      "Build tabular models and DAX that survive audit and scale past the demo.",
      "Design reports people actually open on the second day.",
      "Hand over: documentation, training, and a client team that can maintain it.",
    ],
    qualifications: [
      "3+ years delivering Power BI to external clients.",
      "Advanced DAX and dimensional modelling.",
      "Consulting presence — you can hold a room that disagrees with you.",
      "Professional English and Spanish.",
    ],
    nice: ["PL-300", "Fabric semantic model experience"],
  },
  {
    id: "JR104839", title: "Senior BI Developer — Semantic Models", department: "Analytics",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(9), status: "published", band: "B3",
    summary: "The model layer is where analytics quietly succeeds or fails. You own it.",
    responsibilities: [
      "Build large tabular models with row-level security that holds under audit.",
      "Optimise DAX and storage until refresh windows stop being a topic.",
      "Set the standards other developers on the account follow.",
    ],
    qualifications: [
      "5+ years in BI development, deep in Power BI or Analysis Services.",
      "DAX at the level where you profile before you rewrite.",
      "Professional English and Spanish.",
    ],
    nice: ["PL-300 or DP-600", "Tabular Editor and DAX Studio in daily use"],
  },
  {
    id: "JR104842", title: "Analytics Engineer — dbt & Fabric Warehouse", department: "Analytics",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(3), status: "published", band: "B3",
    summary: "Between the raw data and the report there is a modelling job that deserves software practices. That's this role.",
    responsibilities: [
      "Build dbt projects against Fabric Warehouse and Azure SQL.",
      "Write tests and contracts so downstream breakage is caught before the client sees it.",
      "Document lineage that a non-engineer can follow.",
    ],
    qualifications: [
      "3+ years in analytics engineering or BI with strong SQL.",
      "dbt in production, plus Git discipline.",
      "Professional English and Spanish.",
    ],
    nice: ["dbt certification", "Fabric or Snowflake experience"],
  },
  {
    id: "JR104845", title: "Regulatory Reporting Analyst", department: "Analytics",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(11), status: "published", band: "B3",
    summary: "SUGEF, SUGESE, BCCR. The reports where being late is a finding and being wrong is worse.",
    responsibilities: [
      "Translate regulatory requirements into data models and validation rules.",
      "Automate submissions that are currently a spreadsheet and a long night.",
      "Be the bridge between the compliance team and the engineers.",
    ],
    qualifications: [
      "4+ years in regulatory reporting inside a bank, insurer or regulator.",
      "Strong SQL and Excel; Power BI a plus.",
      "Native Spanish, professional English.",
    ],
    nice: ["Accounting or actuarial background", "Direct experience with SUGEF filings"],
  },
  {
    id: "JR104771", title: "Data Analyst — Early Career", department: "Analytics",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(13), status: "published", band: "B2",
    summary: "For someone one or two years in who wants to learn consulting properly, next to people who will tell you the truth about your work.",
    responsibilities: [
      "Profile and clean client data; document what you find.",
      "Build reports and models under the guidance of a senior consultant.",
      "Write the analysis, not just the query.",
    ],
    qualifications: [
      "1–2 years in analytics, or a strong portfolio.",
      "SQL, Power BI, Python basics.",
      "Intermediate English and native Spanish.",
    ],
    nice: ["PL-300", "Economics, statistics or engineering background"],
  },

  /* ── Governance ──────────────────────────────────────────────── */
  {
    id: "JR104797", title: "Data Governance Lead", department: "Governance",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(4), status: "published", band: "B5",
    summary: "Governance that people follow, not governance that lives in a PDF. You build the frameworks banks and regulators are audited against.",
    responsibilities: [
      "Define data quality, lineage, stewardship and retention frameworks.",
      "Implement Microsoft Purview: catalog, classification, sensitivity labels.",
      "Map controls to ISO/IEC 42001, ISO 27001 and local financial regulation.",
      "Coach client stewards until the framework runs without us.",
    ],
    qualifications: [
      "6+ years in data governance or data management.",
      "Hands-on Purview and Fabric security models.",
      "Working knowledge of LATAM financial regulation.",
      "Professional English and Spanish.",
    ],
    nice: ["CDMP", "ISO 42001 implementation experience"],
  },
  {
    id: "JR104848", title: "Data Quality Engineer", department: "Governance",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(6), status: "published", band: "B3",
    summary: "Find the broken data before the client's board does, and build the thing that keeps finding it.",
    responsibilities: [
      "Profile client data and quantify the damage in terms a CFO understands.",
      "Build rule engines and anomaly detection over structured data in Fabric.",
      "Design the remediation workflow, not just the alert.",
    ],
    qualifications: [
      "4+ years in data quality, data engineering or MDM.",
      "PySpark or Python, SQL, and statistical profiling.",
      "Professional English and Spanish.",
    ],
    nice: ["Great Expectations or Purview DQ", "Anomaly detection experience"],
  },
  {
    id: "JR104851", title: "AI Governance Consultant — ISO/IEC 42001", department: "Governance",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(2), status: "published", band: "B4",
    summary: "Our clients are being asked how they govern AI, often by a regulator, sometimes for the first time. You answer it with them.",
    responsibilities: [
      "Build AI management systems against ISO/IEC 42001 and the NIST AI RMF.",
      "Run AI risk assessments and write the policies that survive contact with engineers.",
      "Prepare institutions for certification and for the questions before it.",
    ],
    qualifications: [
      "5+ years in governance, risk, compliance or audit, with AI or data scope.",
      "You understand enough of how models work to tell a real control from theatre.",
      "Professional English and Spanish.",
    ],
    nice: ["ISO/IEC 42001 or 27001 Lead Implementer", "Public sector experience"],
  },
  {
    id: "JR104854", title: "Information Security Consultant — Data & AI", department: "Governance",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(10), status: "published", band: "B4",
    summary: "Secure the platform without making it unusable. Both halves of that sentence are the job.",
    responsibilities: [
      "Design security for Fabric and Azure data estates: identity, network, labels, keys.",
      "Implement Purview Information Protection and Defender for Cloud coverage.",
      "Review architectures and say no with a workable alternative attached.",
    ],
    qualifications: [
      "5+ years in information security, with cloud data platforms in scope.",
      "Entra ID, private networking, encryption and key management.",
      "Professional English and Spanish.",
    ],
    nice: ["SC-100 or CISSP", "Financial sector audit experience"],
  },
  {
    id: "JR104857", title: "Master Data Management Consultant", department: "Governance",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(14), status: "published", band: "B4",
    summary: "One client, four systems, four versions of the same customer. You end that argument.",
    responsibilities: [
      "Design golden record models, matching rules and survivorship logic.",
      "Implement MDM on Azure or Purview-adjacent tooling.",
      "Build the stewardship process that keeps it true after go-live.",
    ],
    qualifications: [
      "5+ years in MDM or data integration.",
      "Entity resolution, fuzzy matching, and the patience it requires.",
      "Professional English and Spanish.",
    ],
    nice: ["CDMP", "Customer or product domain depth"],
  },

  /* ── Enablement ──────────────────────────────────────────────── */
  {
    id: "JR104784", title: "Technical Trainer — Data & AI", department: "Enablement",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(9), status: "published", band: "B3",
    summary: "Deliver the training that turns a client's team from spectators into operators of their own platform.",
    responsibilities: [
      "Deliver Microsoft Data & AI training to enterprise and public sector teams.",
      "Build labs, exercises and assessments in Spanish and English.",
      "Support certification tracks (DP-600, PL-300, AI-102).",
      "Feed what you learn in the room back into how we deliver projects.",
    ],
    qualifications: [
      "4+ years in data engineering or analytics, plus real teaching experience.",
      "Microsoft Certified Trainer, or ready to become one.",
      "You can teach a skeptical senior engineer and a nervous analyst in the same session.",
      "Professional English and Spanish.",
    ],
    nice: ["Public speaking at community events", "Instructional design background"],
  },
  {
    id: "JR104860", title: "Delivery Manager — Data & AI Projects", department: "Enablement",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(1), status: "published", band: "B4",
    summary: "Own the client relationship and the delivery. When those two pull in opposite directions, you're the one who decides.",
    responsibilities: [
      "Run delivery across concurrent engagements: scope, plan, margin, risk.",
      "Be the escalation point clients trust and the shield the team needs.",
      "Spot the scope creep in week two, not at the retro.",
      "Grow accounts by delivering, not by pitching.",
    ],
    qualifications: [
      "6+ years managing technical delivery, ideally in consulting.",
      "You have read a P&L for your own project and changed something because of it.",
      "Professional English and Spanish.",
    ],
    nice: ["PMP or Scrum Master", "Data or AI project background"],
  },
  {
    id: "JR104863", title: "Technical Pre-Sales Consultant — Microsoft Data & AI", department: "Enablement",
    location: CR, remote: "Hybrid", type: "Full time", postedAt: daysAgo(5), status: "published", band: "B4",
    summary: "Scope what we can actually deliver, and say so before the contract is signed rather than after.",
    responsibilities: [
      "Run discovery and build solution proposals with realistic estimates.",
      "Demo Fabric and Azure AI to technical and executive audiences.",
      "Work with Microsoft account teams on joint opportunities.",
      "Feed delivery reality back into what we promise.",
    ],
    qualifications: [
      "5+ years in data or AI, with client-facing pre-sales or consulting.",
      "You can build the demo yourself, not just narrate it.",
      "Professional English and Spanish.",
    ],
    nice: ["Microsoft partner ecosystem experience", "DP-600 or AZ-305"],
  },
  {
    id: "JR104866", title: "Change & Adoption Consultant", department: "Enablement",
    location: LATAM, remote: "Remote", type: "Full time", postedAt: daysAgo(12), status: "published", band: "B3",
    summary: "The platform works. Nobody's using it. That gap is a discipline, and this is the person who closes it.",
    responsibilities: [
      "Design adoption programmes: champions, comms, training paths, metrics.",
      "Measure usage honestly and act on what it says.",
      "Work with client leadership on the parts that are politics, not product.",
    ],
    qualifications: [
      "4+ years in change management, adoption or L&D in technology rollouts.",
      "You have turned around a rollout that was failing and can say what you changed.",
      "Professional English and Spanish.",
    ],
    nice: ["Prosci or ADKAR certification", "Power Platform or Fabric adoption work"],
  },
];

/* The 25 roles below are the seed for `npm run seed` — one script run
   against a fresh database. In production the source of truth is
   careers.job in Postgres, edited in the recruiter panel. Nothing here is
   read at runtime. */
const blankReqs = () => ({
  yearsRequired: 0, edu: "BACH", bachelorFields: [], masterReq: "NONE",
  masterFields: [], certsRequired: [], certsPreferred: [],
});

export const SEED_JOBS = RAW_JOBS.map((j) => ({ ...blankReqs(), ...REQS[j.id], ...j }));

/* ═══════════════════════════════════════════════════════════════════
   Utilities
   ═══════════════════════════════════════════════════════════════════ */
const postedLabel = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  if (d <= 0) return "Posted today";
  if (d === 1) return "Posted yesterday";
  if (d < 30) return `Posted ${d} days ago`;
  return `Posted ${Math.floor(d / 30)} month${d >= 60 ? "s" : ""} ago`;
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const bucket = (y) => (y <= 2 ? "0–2" : y <= 5 ? "3–5" : y <= 9 ? "6–9" : "10+");
const pct = (arr, p) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const i = (s.length - 1) * p;
  const lo = Math.floor(i), hi = Math.ceil(i);
  return Math.round(s[lo] + (s[hi] - s[lo]) * (i - lo));
};
const median = (arr) => pct(arr, 0.5);
const usd = (n) => `$${Number(n).toLocaleString("en-US")}`;

/* ═══════════════════════════════════════════════════════════════════
   UI atoms
   ═══════════════════════════════════════════════════════════════════ */
const Eyebrow = ({ children, color = BRAND }) => (
  <div className="flex items-center gap-3">
    <span style={{ width: 28, height: 2, background: color }} />
    <span className="text-xs font-semibold uppercase" style={{ color, letterSpacing: "0.18em" }}>{children}</span>
  </div>
);

const Btn = ({ children, onClick, variant = "solid", disabled, full, icon: Icon }) => {
  const styles =
    variant === "solid" ? { background: disabled ? "#C7C2BF" : BRAND, color: "#fff" }
    : variant === "ghost" ? { background: "transparent", color: INK, border: `1px solid ${RULE}` }
    : { background: "transparent", color: BRAND, border: `1px solid ${BRAND}` };
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition-all ${full ? "w-full" : ""} ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:opacity-90"}`}
            style={{ ...styles, borderRadius: 2, fontFamily: FONT }}>
      {Icon && <Icon size={16} />}{children}
    </button>
  );
};

const Field = ({ label, children, required, hint, error }) => (
  <label className="block mb-5">
    <span className="block text-xs font-semibold mb-2" style={{ color: INK, letterSpacing: "0.04em" }}>
      {label} {required && <span style={{ color: BRAND }}>*</span>}
    </span>
    {children}
    {hint && !error && <span className="block text-xs mt-1.5" style={{ color: MUTED }}>{hint}</span>}
    {error && <span className="flex items-center gap-1.5 text-xs mt-1.5" style={{ color: BRAND }}><AlertCircle size={12} /> {error}</span>}
  </label>
);

const inputStyle = {
  width: "100%", padding: "11px 13px", border: `1px solid ${RULE}`, borderRadius: 2,
  fontSize: 14, fontFamily: FONT, color: INK, background: "#fff", outline: "none",
};
const Input = (p) => <input {...p} style={{ ...inputStyle, ...(p.style || {}) }} />;
const Select = ({ children, ...p }) => <select {...p} style={{ ...inputStyle, ...(p.style || {}) }}>{children}</select>;
const Textarea = (p) => <textarea {...p} style={{ ...inputStyle, minHeight: 120, resize: "vertical", ...(p.style || {}) }} />;

const StatusPill = ({ status }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold shrink-0"
        style={{ color: STATUS_COLOR[status], background: `${STATUS_COLOR[status]}14`, borderRadius: 2 }}>
    <span style={{ width: 5, height: 5, borderRadius: 9, background: STATUS_COLOR[status] }} />
    {status}
  </span>
);

/* ═══════════════════════════════════════════════════════════════════
   Signature: the linchpin constellation — spokes filter the board
   ═══════════════════════════════════════════════════════════════════ */
const NODES = [
  { key: "Data platforms", x: 118, y: 76, anchor: "start", lx: 118, ly: 56 },
  { key: "AI & GenAI", x: 482, y: 76, anchor: "end", lx: 482, ly: 56 },
  { key: "Analytics", x: 74, y: 268, anchor: "start", lx: 74, ly: 296 },
  { key: "Governance", x: 526, y: 268, anchor: "end", lx: 526, ly: 296 },
  { key: "Enablement", x: 300, y: 330, anchor: "middle", lx: 300, ly: 356 },
];

const Constellation = ({ active, onPick, counts }) => (
  <svg viewBox="0 0 600 380" className="w-full" style={{ maxHeight: 320 }} role="img"
       aria-label="Practice areas. Select one to filter open roles.">
    {NODES.map((n, i) => {
      const on = active === n.key;
      return (
        <g key={n.key}>
          <line x1={300} y1={190} x2={n.x} y2={n.y} stroke={on ? BRAND : "#5A1E1B"}
                strokeWidth={on ? 1.6 : 0.9} className="lp-line" style={{ animationDelay: `${i * 90}ms` }} />
          <g onClick={() => onPick(on ? "" : n.key)} style={{ cursor: "pointer" }} tabIndex={0} role="button"
             aria-pressed={on} className="lp-node" onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onPick(on ? "" : n.key)}>
            <circle cx={n.x} cy={n.y} r={16} fill="transparent" />
            <circle cx={n.x} cy={n.y} r={on ? 7 : 5.5} fill={on ? BRAND : "transparent"} stroke={BRAND} strokeWidth={1.4} />
            <text x={n.lx} y={n.ly} textAnchor={n.anchor} fill={on ? "#fff" : "#B9B0AE"}
                  style={{ fontSize: 13, fontFamily: FONT, fontWeight: on ? 600 : 400 }}>{n.key}</text>
            <text x={n.lx} y={n.ly + 15} textAnchor={n.anchor} fill={on ? BRAND : "#6A5C5A"}
                  style={{ fontSize: 10.5, fontFamily: FONT, letterSpacing: "0.08em" }}>{counts[n.key] || 0} open</text>
          </g>
        </g>
      );
    })}
    <circle cx={300} cy={190} r={22} fill="none" stroke={BRAND} strokeWidth={1.2} />
    <circle cx={300} cy={190} r={13} fill={BRAND} opacity={0.15} />
    <rect x={297} y={180} width={6} height={20} rx={3} fill="#fff" />
    <text x={300} y={158} textAnchor="middle" fill="#fff" style={{ fontSize: 13, fontWeight: 600, fontFamily: FONT }}>Linchpin</text>
  </svg>
);

/* ═══════════════════════════════════════════════════════════════════
   Header
   ═══════════════════════════════════════════════════════════════════ */
const Header = ({ go }) => (
  <header className="sticky top-0 z-40 flex items-center justify-between px-5 md:px-10 py-4"
          style={{ background: "#fff", borderBottom: `1px solid ${RULE}` }}>
    <div className="flex items-center gap-2 cursor-pointer" onClick={() => go({ name: "jobs" })}>
      <span style={{ width: 9, height: 9, borderRadius: 9, background: BRAND, display: "inline-block" }} />
      <span className="font-bold tracking-tight" style={{ color: INK, fontSize: 17 }}>Linchpin People</span>
      <span className="hidden sm:inline text-xs font-medium ml-1" style={{ color: MUTED }}>Careers</span>
    </div>
    <button onClick={() => go({ name: "admin" })}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold"
            style={{ color: "#fff", background: INK, borderRadius: 2 }}>
      <Shield size={13} /> Recruiter
    </button>
  </header>
);

/* ═══════════════════════════════════════════════════════════════════
   Jobs board
   ═══════════════════════════════════════════════════════════════════ */
const PAGE = 12;

function JobsBoard({ jobs, go }) {
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [loc, setLoc] = useState("");
  const [type, setType] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [shown, setShown] = useState(PAGE);

  const open = jobs.filter((j) => j.status === "published");
  const counts = useMemo(() => {
    const c = {};
    open.forEach((j) => (c[j.department] = (c[j.department] || 0) + 1));
    return c;
  }, [jobs]);
  const locations = useMemo(() => [...new Set(open.map((j) => j.location))].sort(), [jobs]);

  const results = open.filter((j) => {
    const hay = `${j.title} ${j.department} ${j.summary} ${j.id}`.toLowerCase();
    return (!q || hay.includes(q.toLowerCase())) && (!dept || j.department === dept) &&
           (!loc || j.location === loc) && (!type || j.remote === type);
  });
  useEffect(() => { setShown(PAGE); }, [q, dept, loc, type]);

  const clear = () => { setQ(""); setDept(""); setLoc(""); setType(""); };
  const activeCount = [q, dept, loc, type].filter(Boolean).length;

  return (
    <div>
      <section style={{ background: NEAR_BLACK }} className="px-5 md:px-10 pt-10 pb-2">
        <div className="max-w-5xl mx-auto">
          <Eyebrow>Careers · Data &amp; AI · Latin America</Eyebrow>
          <h1 className="mt-5 font-bold" style={{ color: "#fff", fontSize: "clamp(30px,5.5vw,52px)", lineHeight: 1.08, letterSpacing: "-0.02em" }}>
            The work is the <span style={{ color: BRAND }}>hard part</span> of data.
          </h1>
          <p className="mt-4 max-w-xl" style={{ color: "#A9A2A0", fontSize: 15, lineHeight: 1.65 }}>
            Banks, regulators and healthcare institutions across Latin America hand us the data they
            cannot afford to get wrong. {open.length} roles open. Pick where you'd fit.
          </p>
          <div className="mt-2"><Constellation active={dept} onPick={setDept} counts={counts} /></div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-6" style={{ background: PAPER, borderBottom: `1px solid ${RULE}` }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} style={{ position: "absolute", left: 13, top: 14, color: MUTED }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search jobs"
                     placeholder="Search roles, skills or job ID" style={{ paddingLeft: 38 }} />
            </div>
            <button onClick={() => setShowFilters((s) => !s)} className="flex items-center gap-2 px-4 text-sm font-semibold"
                    style={{ border: `1px solid ${RULE}`, background: "#fff", color: INK, borderRadius: 2 }}>
              <Filter size={14} />
              <span className="hidden sm:inline">Refine your search</span>
              {activeCount > 0 && <span className="px-1.5 text-xs font-bold" style={{ background: BRAND, color: "#fff", borderRadius: 2 }}>{activeCount}</span>}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 p-4 grid gap-3 sm:grid-cols-3" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
              <div>
                <span className="block text-xs font-semibold mb-2" style={{ color: INK }}>Practice area</span>
                <Select value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">All areas</option>
                  {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
                </Select>
              </div>
              <div>
                <span className="block text-xs font-semibold mb-2" style={{ color: INK }}>Location</span>
                <Select value={loc} onChange={(e) => setLoc(e.target.value)}>
                  <option value="">All locations</option>
                  {locations.map((l) => <option key={l}>{l}</option>)}
                </Select>
              </div>
              <div>
                <span className="block text-xs font-semibold mb-2" style={{ color: INK }}>Work setup</span>
                <Select value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">Any setup</option>
                  <option>Remote</option><option>Hybrid</option><option>On site</option>
                </Select>
              </div>
              {activeCount > 0 && (
                <button onClick={clear} className="text-xs font-semibold text-left sm:col-span-3" style={{ color: BRAND }}>Clear all filters</button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="px-5 md:px-10 py-8" style={{ background: "#fff" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold uppercase mb-5" style={{ color: MUTED, letterSpacing: "0.14em" }}>
            {results.length} {results.length === 1 ? "job" : "jobs"} found
            {dept && <span style={{ color: BRAND }}> · {dept}</span>}
          </div>
          {results.length === 0 ? (
            <div className="py-16 text-center">
              <p className="font-semibold mb-2" style={{ color: INK }}>No roles match those filters.</p>
              <p className="text-sm mb-5" style={{ color: MUTED }}>Widen the search, or check back — we post regularly.</p>
              <Btn variant="outline" onClick={clear}>Clear filters</Btn>
            </div>
          ) : (
            <div>
              {results.slice(0, shown).map((j) => (
                <div key={j.id} onClick={() => go({ name: "job", id: j.id })} className="group py-6 cursor-pointer"
                     style={{ borderTop: `1px solid ${RULE}` }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold group-hover:underline" style={{ color: BRAND, fontSize: 17, textUnderlineOffset: 4 }}>{j.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-xs" style={{ color: MUTED }}>
                        <span className="flex items-center gap-1.5"><MapPin size={13} /> {j.location}</span>
                        <span className="flex items-center gap-1.5"><Briefcase size={13} /> {j.remote} · {j.type}</span>
                        <span className="flex items-center gap-1.5"><Clock size={13} /> {postedLabel(j.postedAt)}</span>
                      </div>
                      <div className="mt-2 text-xs font-medium" style={{ color: "#9A9290" }}>{j.id}</div>
                    </div>
                    <ChevronRight size={18} style={{ color: RULE, marginTop: 4 }} className="shrink-0" />
                  </div>
                </div>
              ))}
              <div style={{ borderTop: `1px solid ${RULE}` }} />
              {shown < results.length && (
                <div className="pt-7 text-center">
                  <Btn variant="ghost" onClick={() => setShown((s) => s + PAGE)}>
                    Show {Math.min(PAGE, results.length - shown)} more roles
                  </Btn>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Job detail
   ═══════════════════════════════════════════════════════════════════ */
const listOf = (arr) => (arr.length === 1 ? arr[0]
  : `${arr.slice(0, -1).join(", ")} or ${arr[arr.length - 1]}`);

const Req = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:gap-4">
    <span className="text-xs font-bold shrink-0" style={{ color: BRAND, width: 108, letterSpacing: "0.04em" }}>{label}</span>
    <span className="text-sm" style={{ color: "#4A4444", lineHeight: 1.55 }}>{children}</span>
  </div>
);

function JobDetail({ job, go, certs = [] }) {
  const [copied, setCopied] = useState(false);
  if (!job) return null;
  // Share link uses the /j/{id} page, which serves per-job Open Graph tags for
  // a rich LinkedIn card and then redirects a human to the real detail page.
  const shareUrl = `${window.location.origin}/j/${job.id}`;
  const share = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked; nothing to do */ }
  };
  const Section = ({ title, items }) => (
    <div className="mb-8">
      <h4 className="text-xs font-bold uppercase mb-4" style={{ color: INK, letterSpacing: "0.12em" }}>{title}</h4>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-3 text-sm" style={{ color: "#4A4444", lineHeight: 1.6 }}>
            <span style={{ marginTop: 7, width: 5, height: 5, borderRadius: 5, background: BRAND, flexShrink: 0 }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <div style={{ background: "#fff" }}>
      <div className="px-5 md:px-10 py-8 max-w-3xl mx-auto">
        <button onClick={() => go({ name: "jobs" })} className="flex items-center gap-1.5 text-xs font-semibold mb-6" style={{ color: MUTED }}>
          <ChevronLeft size={14} /> All jobs
        </button>
        <Eyebrow>{job.department}</Eyebrow>
        <h1 className="mt-4 font-bold" style={{ color: INK, fontSize: "clamp(24px,4vw,36px)", lineHeight: 1.15, letterSpacing: "-0.02em" }}>{job.title}</h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-xs" style={{ color: MUTED }}>
          <span className="flex items-center gap-1.5"><MapPin size={13} /> {job.location}</span>
          <span className="flex items-center gap-1.5"><Briefcase size={13} /> {job.remote} · {job.type}</span>
          <span className="flex items-center gap-1.5"><Clock size={13} /> {postedLabel(job.postedAt)}</span>
          <span style={{ color: "#9A9290" }}>{job.id}</span>
        </div>
        <div className="my-7 flex flex-wrap gap-3">
          <Btn onClick={() => go({ name: "apply", id: job.id })}>Apply for this job</Btn>
          <Btn variant="ghost" icon={copied ? Check : Link2} onClick={share}>{copied ? "Link copied" : "Copy share link"}</Btn>
        </div>
        <p className="text-base mb-8" style={{ color: "#4A4444", lineHeight: 1.7 }}>{job.summary}</p>
        <Section title="What you'll do" items={job.responsibilities} />

        {/* Requirements are structured, so they render the same on every
            posting instead of hiding in a paragraph someone wrote at 6pm. */}
        <div className="mb-8 p-5" style={{ background: PAPER, borderRadius: 2 }}>
          <h4 className="text-xs font-bold uppercase mb-4" style={{ color: INK, letterSpacing: "0.12em" }}>Requirements</h4>
          <div className="space-y-3">
            <Req label="Experience">
              {job.yearsRequired > 0 ? `${job.yearsRequired}+ years of relevant experience` : "Open to early-career candidates"}
            </Req>
            {job.edu && job.edu !== "NONE" && (
              <Req label="Education">
                {eduLevel(job.edu).label}
                {job.bachelorFields?.length > 0 && ` in ${listOf(job.bachelorFields)}`}
              </Req>
            )}
            {job.masterReq && job.masterReq !== "NONE" && (
              <Req label="Postgraduate">
                {`Master's degree ${job.masterReq === "REQUIRED" ? "required" : "preferred"}`}
                {job.masterFields?.length > 0 && ` — ${listOf(job.masterFields)}`}
              </Req>
            )}
            {job.certsRequired?.length > 0 && (
              <Req label="Certifications">{listOf(certNames(certs, job.certsRequired))} — required</Req>
            )}
            {job.certsPreferred?.length > 0 && (
              <Req label="Also valued">{listOf(certNames(certs, job.certsPreferred))}</Req>
            )}
          </div>
        </div>

        <Section title="What we're looking for" items={job.qualifications} />
        {job.nice?.length > 0 && <Section title="Nice to have" items={job.nice} />}
        <div className="pt-6 mt-4" style={{ borderTop: `1px solid ${RULE}` }}>
          <p className="text-xs" style={{ color: MUTED, lineHeight: 1.7 }}>
            Linchpin People hires on evidence of ability. We review every application against the role's
            requirements and reply either way. If you need an adjustment to the process, tell us in your
            application and we'll arrange it.
          </p>
        </div>
        <div className="mt-8"><Btn onClick={() => go({ name: "apply", id: job.id })}>Apply for this job</Btn></div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Apply — no account, one step
   ═══════════════════════════════════════════════════════════════════ */
const MAX_MB = 2;

/* Cloudflare Turnstile. Renders only when a site key is configured; otherwise
   it is absent and the form submits exactly as before. */
function Turnstile({ siteKey, onToken }) {
  const box = useRef(null);
  const widget = useRef(null);
  useEffect(() => {
    if (!siteKey) return undefined;
    let dead = false;
    const mount = () => {
      if (dead || !window.turnstile || !box.current || widget.current !== null) return;
      widget.current = window.turnstile.render(box.current, {
        sitekey: siteKey,
        callback: (t) => onToken(t),
        "error-callback": () => onToken(""),
        "expired-callback": () => onToken(""),
      });
    };
    if (window.turnstile) mount();
    else {
      let s = document.querySelector("script[data-turnstile]");
      if (!s) {
        s = document.createElement("script");
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true; s.defer = true; s.dataset.turnstile = "1";
        document.head.appendChild(s);
      }
      s.addEventListener("load", mount);
    }
    return () => {
      dead = true;
      if (widget.current !== null && window.turnstile) {
        try { window.turnstile.remove(widget.current); } catch { /* already gone */ }
        widget.current = null;
      }
    };
  }, [siteKey, onToken]);
  if (!siteKey) return null;
  return <div ref={box} className="mb-4" />;
}

function ApplyForm({ job, onSubmit, go, certs: catalog = [], turnstileSiteKey = null }) {
  const [f, setF] = useState({
    firstName: "", lastName: "", email: "", phone: "", country: "", linkedin: "",
    years: "", salary: "", notice: "", authorized: "", travel: "", source: "", cover: "", consent: false,
  });
  const [certs, setCerts] = useState([]);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggleCert = (c) => setCerts((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const pick = (e) => {
    const fl = e.target.files?.[0];
    if (!fl) return;
    if (fl.size > MAX_MB * 1024 * 1024) {
      setErr((s) => ({ ...s, file: `That file is ${(fl.size / 1048576).toFixed(1)} MB. The limit is ${MAX_MB} MB.` }));
      return;
    }
    setErr((s) => ({ ...s, file: undefined }));
    setFile(fl);
  };

  const submit = async () => {
    const e = {};
    if (!f.firstName.trim()) e.firstName = "Required.";
    if (!f.lastName.trim()) e.lastName = "Required.";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Enter a valid email address.";
    if (!f.phone.trim()) e.phone = "Required.";
    if (!f.country) e.country = "Select where you live.";
    if (f.years === "" || isNaN(+f.years) || +f.years < 0 || +f.years > 50) e.years = "Enter a number between 0 and 50.";
    const sal = +String(f.salary).replace(/[,\s$]/g, "");
    if (f.salary === "" || !Number.isFinite(sal) || !Number.isInteger(sal))
      e.salary = "Enter your expectation in USD per month.";
    else if (sal < SALARY_MIN || sal > SALARY_MAX)
      e.salary = `Enter a monthly figure between ${usd(SALARY_MIN)} and ${usd(SALARY_MAX)}.`;
    if (!f.authorized) e.authorized = "Select one.";
    if (!f.travel) e.travel = "Select one.";
    if (!file) e.file = "Attach your resume to continue.";
    if (!f.consent) e.consent = "We need your consent to store your data.";
    if (turnstileSiteKey && !captchaToken) e.submit = "Please complete the verification below and try again.";
    setErr(e);
    if (Object.keys(e).length) return;

    setBusy(true);
    const ref = await onSubmit({
      ...f, years: +f.years, salary: +String(f.salary).replace(/[,\s$]/g, ""), certs, captchaToken,
      jobId: job.id, jobTitle: job.title, department: job.department,
    }, file);
    setBusy(false);
    if (ref?.error) {
      setErr({ submit: ref.error });
      // The Turnstile token is single-use; a retry needs a fresh one.
      if (turnstileSiteKey) { try { window.turnstile?.reset(); } catch { /* ignore */ } setCaptchaToken(""); }
    }
  };

  return (
    <div style={{ background: PAPER }} className="px-5 py-10">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => go({ name: "job", id: job.id })} className="flex items-center gap-1.5 text-xs font-semibold mb-6" style={{ color: MUTED }}>
          <ChevronLeft size={14} /> Back to the job
        </button>
        <div className="p-6 md:p-8" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
          <Eyebrow>{job.id} · {job.department}</Eyebrow>
          <h2 className="mt-4 mb-1 font-bold" style={{ color: INK, fontSize: 24, letterSpacing: "-0.01em" }}>Apply — {job.title}</h2>
          <p className="text-sm mb-8" style={{ color: MUTED }}>
            No account, no password. Four minutes. Everything marked with an asterisk is required.
          </p>

          <h4 className="text-xs font-bold uppercase mb-4 pb-2" style={{ color: INK, letterSpacing: "0.12em", borderBottom: `1px solid ${RULE}` }}>About you</h4>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="First name" required error={err.firstName}><Input value={f.firstName} onChange={set("firstName")} /></Field>
            <Field label="Last name" required error={err.lastName}><Input value={f.lastName} onChange={set("lastName")} /></Field>
            <Field label="Email" required error={err.email} hint="Where we'll reply."><Input type="email" value={f.email} onChange={set("email")} /></Field>
            <Field label="Phone" required error={err.phone}><Input value={f.phone} onChange={set("phone")} placeholder="+506 8888 8888" /></Field>
            <Field label="Country of residence" required error={err.country} hint="Where you live now, not where you're from.">
              <Select value={f.country} onChange={set("country")}>
                <option value="">Select</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="LinkedIn profile"><Input value={f.linkedin} onChange={set("linkedin")} placeholder="linkedin.com/in/…" /></Field>
          </div>

          <h4 className="text-xs font-bold uppercase mt-6 mb-4 pb-2" style={{ color: INK, letterSpacing: "0.12em", borderBottom: `1px solid ${RULE}` }}>Your resume</h4>
          <Field label="Resume" required error={err.file} hint={`PDF, DOC or DOCX. Up to ${MAX_MB} MB.`}>
            <div onClick={() => fileRef.current?.click()} className="flex items-center gap-3 px-4 py-5 cursor-pointer"
                 style={{ border: `1px dashed ${file ? BRAND : RULE}`, background: file ? "#FDF6F5" : "#FAFAFA", borderRadius: 2 }}>
              {file ? <FileText size={20} style={{ color: BRAND }} /> : <Upload size={20} style={{ color: MUTED }} />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: file ? INK : MUTED }}>{file ? file.name : "Choose a file"}</div>
                {file && <div className="text-xs mt-0.5" style={{ color: MUTED }}>{(file.size / 1024).toFixed(0)} KB</div>}
              </div>
              {file && <button onClick={(e) => { e.stopPropagation(); setFile(null); }} style={{ color: MUTED }} title="Remove file"><X size={16} /></button>}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pick} style={{ display: "none" }} />
          </Field>
          <Field label="Why this role" hint="Optional. A short paragraph beats a long one.">
            <Textarea value={f.cover} onChange={set("cover")} maxLength={1500} placeholder="What in your work so far lines up with this role?" />
          </Field>

          <h4 className="text-xs font-bold uppercase mt-6 mb-4 pb-2" style={{ color: INK, letterSpacing: "0.12em", borderBottom: `1px solid ${RULE}` }}>
            Experience and certifications
          </h4>
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Field label="Years of relevant experience" required error={err.years} hint="A whole number.">
              <Input type="number" min="0" max="50" value={f.years} onChange={set("years")} placeholder="5" />
            </Field>
            <Field label="Notice period">
              <Select value={f.notice} onChange={set("notice")}>
                <option value="">Select</option>
                <option>Immediate</option><option>2 weeks</option><option>1 month</option><option>2 months or more</option>
              </Select>
            </Field>
          </div>
          {catalog.length > 0 && (
            <Field label="Certifications you currently hold" hint="Select all that apply.">
              <div className="flex flex-wrap gap-2 mt-1">
                {catalog.map((c) => {
                  const on = certs.includes(c.code);
                  return (
                    <button key={c.code} onClick={() => toggleCert(c.code)} className="px-3 py-2 text-xs font-medium text-left"
                            style={{ border: `1px solid ${on ? BRAND : RULE}`, borderRadius: 2, background: on ? "#FDF6F5" : "#fff", color: on ? BRAND : INK }}>
                      {on && <Check size={11} style={{ display: "inline", marginRight: 5, marginTop: -2 }} />}{c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <div className="grid sm:grid-cols-2 gap-x-4 mt-2">
            <Field label="Salary expectation" required error={err.salary}
                   hint="Gross monthly, in US dollars. Give us the figure you'd accept, not a placeholder — it decides whether we're in the same range before either of us spends time on it.">
              <div className="relative">
                <span className="absolute font-semibold" style={{ left: 13, top: 11, color: MUTED, fontSize: 14 }}>$</span>
                <Input value={f.salary} onChange={set("salary")} inputMode="numeric" aria-label="Salary expectation in USD per month"
                       style={{ paddingLeft: 26, paddingRight: 82 }} />
                <span className="absolute text-xs" style={{ right: 13, top: 13, color: MUTED }}>USD / month</span>
              </div>
            </Field>
            <Field label="Authorized to work in the role's country" required error={err.authorized}>
              <Select value={f.authorized} onChange={set("authorized")}>
                <option value="">Select</option>
                <option>Yes</option>
                <option>No — I would need sponsorship</option>
              </Select>
            </Field>
            <Field label="Able to travel for client work" required error={err.travel}
                   hint="Some of our work happens inside a bank or a ministry. Answer for what you can actually commit to — we staff against this.">
              <Select value={f.travel} onChange={set("travel")}>
                <option value="">Select</option>
                {TRAVEL.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
              </Select>
            </Field>
            <Field label="How did you hear about us">
              <Select value={f.source} onChange={set("source")}>
                <option value="">Select</option>
                <option>LinkedIn</option><option>Data Experts Latam</option><option>Referral</option>
                <option>Microsoft partner network</option><option>Event or meetup</option><option>Other</option>
              </Select>
            </Field>
          </div>

          <div className="mt-2 mb-6 p-4" style={{ background: PAPER, borderRadius: 2 }}>
            <label className="flex gap-3 cursor-pointer">
              <input type="checkbox" checked={f.consent} onChange={(e) => setF((s) => ({ ...s, consent: e.target.checked }))}
                     style={{ marginTop: 3, accentColor: BRAND, width: 15, height: 15 }} />
              <span className="text-xs" style={{ color: "#4A4444", lineHeight: 1.6 }}>
                I agree that Linchpin People may store and process my resume and the details above to evaluate
                me for this role and for similar openings for up to 12 months. I can ask for my data to be
                deleted at any time by writing to privacy@linchpinpeople.com.
              </span>
            </label>
            {err.consent && <span className="flex items-center gap-1.5 text-xs mt-2" style={{ color: BRAND }}><AlertCircle size={12} /> {err.consent}</span>}
          </div>

          <Turnstile siteKey={turnstileSiteKey} onToken={setCaptchaToken} />

          {err.submit && (
            <div className="flex items-center gap-2 mb-4 px-4 py-3 text-xs font-semibold" style={{ background: "#FDF2F2", color: BRAND, borderRadius: 2 }}>
              <AlertCircle size={14} /> {err.submit}
            </div>
          )}
          <div className="flex gap-3">
            <Btn onClick={submit} disabled={busy} icon={busy ? Loader2 : undefined}>{busy ? "Submitting…" : "Submit application"}</Btn>
            <Btn variant="ghost" onClick={() => go({ name: "job", id: job.id })}>Cancel</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

const Confirmation = ({ ref_, job, go }) => (
  <div style={{ background: PAPER, minHeight: "70vh" }} className="px-5 py-16">
    <div className="max-w-md mx-auto p-8 text-center" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
      <div className="mx-auto flex items-center justify-center" style={{ width: 44, height: 44, background: "#F0F7F1", borderRadius: 44 }}>
        <Check size={22} style={{ color: "#166534" }} />
      </div>
      <h2 className="mt-5 mb-2 font-bold" style={{ color: INK, fontSize: 22 }}>Application received</h2>
      <p className="text-sm mb-6" style={{ color: MUTED, lineHeight: 1.65 }}>
        We're reviewing you for <strong style={{ color: INK }}>{job?.title}</strong>. You'll hear from us
        either way — usually within ten working days.
      </p>
      <div className="px-4 py-3 mb-6" style={{ background: PAPER }}>
        <div className="text-xs font-semibold uppercase mb-1" style={{ color: MUTED, letterSpacing: "0.12em" }}>Your reference</div>
        <div className="font-bold" style={{ color: BRAND, fontSize: 18, letterSpacing: "0.04em" }}>{ref_}</div>
      </div>
      <p className="text-xs mb-6" style={{ color: MUTED }}>Quote it if you write to us about this application.</p>
      <Btn variant="outline" full onClick={() => go({ name: "jobs" })}>Back to open roles</Btn>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   Admin gate — there is no passcode. Static Web Apps closes /api/admin/*
   at the edge on the `recruiter` role, and this screen only explains why
   someone is looking at it. A client-side check is a signpost, not a lock.
   ═══════════════════════════════════════════════════════════════════ */
function AdminGate({ me, go }) {
  return (
    <div className="px-5 py-16" style={{ background: NEAR_BLACK, minHeight: "70vh" }}>
      <div className="max-w-sm mx-auto p-7" style={{ background: "#fff" }}>
        <Eyebrow>Internal</Eyebrow>
        <h2 className="mt-4 mb-1 font-bold" style={{ color: INK, fontSize: 22 }}>Recruiter access</h2>
        {!me ? (
          <>
            <p className="text-sm mb-6" style={{ color: MUTED, lineHeight: 1.6 }}>
              Sign in with your Linchpin account.
            </p>
            <Btn full onClick={() => { window.location.href = api.loginUrl("/"); }}>
              Sign in with Microsoft
            </Btn>
          </>
        ) : (
          <>
            <p className="text-sm mb-2" style={{ color: MUTED, lineHeight: 1.6 }}>
              You're signed in as <strong style={{ color: INK }}>{me.name}</strong>, but your account
              doesn't have the recruiter role.
            </p>
            <p className="text-xs mb-6" style={{ color: MUTED, lineHeight: 1.6 }}>
              Someone who administers the site can grant it. Ask them rather than trying again.
            </p>
            <Btn variant="ghost" full onClick={() => { window.location.href = api.logoutUrl(); }}>
              Sign out
            </Btn>
          </>
        )}
        <button onClick={() => go({ name: "jobs" })} className="w-full text-xs font-semibold mt-4" style={{ color: MUTED }}>
          Back to careers
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Analytics — the figure is required, so every chart covers the whole
   pool. No "declined to say" hole, no self-selected sample.
   ═══════════════════════════════════════════════════════════════════ */
const Card = ({ title, subtitle, children, icon: Icon }) => (
  <div className="p-5" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
    <div className="flex items-start gap-2 mb-1">
      {Icon && <Icon size={14} style={{ color: BRAND, marginTop: 2 }} />}
      <h4 className="text-xs font-bold uppercase" style={{ color: INK, letterSpacing: "0.1em" }}>{title}</h4>
    </div>
    {subtitle && <p className="text-xs mb-4" style={{ color: MUTED }}>{subtitle}</p>}
    {children}
  </div>
);

const Stat = ({ label, value, sub }) => (
  <div className="p-4" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
    <div className="text-xs font-semibold uppercase mb-2" style={{ color: MUTED, letterSpacing: "0.1em" }}>{label}</div>
    <div className="font-bold" style={{ color: INK, fontSize: 22, letterSpacing: "-0.02em" }}>{value}</div>
    {sub && <div className="text-xs mt-1" style={{ color: MUTED }}>{sub}</div>}
  </div>
);

const tip = {
  contentStyle: { fontFamily: FONT, fontSize: 12, border: `1px solid ${RULE}`, borderRadius: 2, boxShadow: "none" },
  cursor: { fill: "#00000008" },
};
const axis = { tick: { fontSize: 11, fontFamily: FONT, fill: MUTED }, axisLine: { stroke: RULE }, tickLine: false };
const legendStyle = { fontSize: 11, fontFamily: FONT };

function Analytics({ apps, jobs, certs = [] }) {
  const [fJob, setFJob] = useState("");
  const rows = fJob ? apps.filter((a) => a.jobId === fJob) : apps;
  const salaries = rows.map((a) => a.salary).filter(Boolean);

  const byRole = useMemo(() => jobs.map((j) => ({
    name: j.title.split(" — ")[0], full: j.title, count: apps.filter((a) => a.jobId === j.id).length,
  })).filter((r) => r.count > 0).sort((a, b) => b.count - a.count).slice(0, 12), [apps, jobs]);

  const byStatus = useMemo(() => STATUSES.map((s) => ({
    name: s, count: rows.filter((a) => a.status === s).length,
  })).filter((s) => s.count > 0), [rows]);

  const byExp = useMemo(() => {
    const b = { "0–2": 0, "3–5": 0, "6–9": 0, "10+": 0 };
    rows.forEach((a) => (b[bucket(a.years)] += 1));
    return Object.entries(b).map(([name, count]) => ({ name, count }));
  }, [rows]);

  const byCert = useMemo(() => {
    const c = {};
    rows.forEach((a) => (a.certs || []).forEach((x) => (c[x] = (c[x] || 0) + 1)));
    return Object.entries(c).map(([k, count]) => ({ name: k, full: certName(certs, k), count }))
                 .sort((a, b) => b.count - a.count).slice(0, 8);
  }, [rows]);

  /* Histogram. Buckets in ladder order, never sorted by count — a gap in
     the middle is the finding, and re-sorting would hide it. */
  const dist = useMemo(() => BANDS.map((b) => ({
    name: b.short, label: b.label, color: b.color,
    count: rows.filter((a) => a.salary && bucketOf(a.salary).code === b.code).length,
  })), [rows]);

  const scatter = useMemo(
    () => rows.filter((a) => a.salary).map((a) => ({ x: a.years, y: a.salary })), [rows]);

  /* Median per role with the interquartile range drawn on it. A median
     alone invites someone to read it as "the price"; the bar shows the
     spread it came from. */
  const byRoleSalary = useMemo(() => jobs.map((j) => {
    const pool = apps.filter((a) => a.jobId === j.id && a.salary).map((a) => a.salary);
    if (pool.length < 2) return null;
    const med = median(pool);
    return {
      name: j.title.split(" — ")[0], full: j.title, med, n: pool.length,
      err: [med - pct(pool, 0.25), pct(pool, 0.75) - med],
      budget: j.band ? band(j.band).label : null,
    };
  }).filter(Boolean).sort((a, b) => b.med - a.med).slice(0, 10), [apps, jobs]);

  const byCountry = useMemo(() => {
    const c = {};
    rows.forEach((a) => a.country && (c[a.country] = (c[a.country] || 0) + 1));
    return Object.entries(c).map(([code, count]) => ({ name: country(code)?.name || code, code, count }))
                 .sort((a, b) => b.count - a.count).slice(0, 8);
  }, [rows]);

  /* Travel in ladder order, never by count. "Not able" being the tallest
     bar on a role that needs client-site work is the whole point. */
  const byTravel = useMemo(() => TRAVEL.map((t) => ({
    name: t.short, label: t.label, color: t.color,
    count: rows.filter((a) => a.travel === t.code).length,
  })), [rows]);

  const mobile = rows.filter((a) => TRAVELS.includes(a.travel)).length;
  const certed = rows.filter((a) => (a.certs || []).length > 0).length;

  if (apps.length === 0) {
    return (
      <div className="px-5 md:px-10 py-8 max-w-5xl mx-auto">
        <div className="p-12 text-center" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
          <BarChart3 size={26} style={{ color: RULE, margin: "0 auto 14px" }} />
          <p className="font-semibold mb-1" style={{ color: INK }}>No applications to analyze yet.</p>
          <p className="text-sm" style={{ color: MUTED }}>These charts fill in as candidates apply.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 md:px-10 py-8 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <Select value={fJob} onChange={(e) => setFJob(e.target.value)} style={{ width: "auto", minWidth: 240 }}>
          <option value="">All roles</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Stat label="Candidates" value={rows.length} sub={fJob ? "in this role" : `across ${byRole.length} roles`} />
        <Stat label="Median experience" value={`${median(rows.map((a) => a.years))} yr`} />
        <Stat label="Median expectation" value={salaries.length ? usd(median(salaries)) : "—"}
              sub={salaries.length ? `${usd(pct(salaries, 0.25))} – ${usd(pct(salaries, 0.75))} middle half` : "—"} />
        <Stat label="Certified" value={`${Math.round((certed / rows.length) * 100)}%`} sub={`${certed} of ${rows.length}`} />
        <Stat label="Can travel" value={`${Math.round((mobile / rows.length) * 100)}%`}
              sub="regularly or more" />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        {!fJob && (
          <Card title="Applications by role" icon={Users} subtitle="Top 12. Where the interest is landing.">
            <ResponsiveContainer width="100%" height={Math.max(160, byRole.length * 28)}>
              <BarChart data={byRole} layout="vertical" margin={{ left: 0, right: 24 }}>
                <XAxis type="number" {...axis} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={132} {...axis} />
                <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} />
                <Bar dataKey="count" fill={BRAND} radius={[0, 2, 2, 0]} barSize={13}>
                  <LabelList dataKey="count" position="right" style={{ fontSize: 10, fontFamily: FONT, fill: MUTED }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card title="Pipeline" icon={BarChart3} subtitle="Where candidates sit in the process.">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={byStatus} margin={{ left: -18, right: 8 }}>
              <XAxis dataKey="name" {...axis} interval={0} angle={-18} textAnchor="end" height={54} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]} barSize={30}>
                {byStatus.map((s) => <Cell key={s.name} fill={STATUS_COLOR[s.name]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Experience distribution" icon={Users} subtitle="Years of relevant experience, grouped.">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={byExp} margin={{ left: -18, right: 8 }}>
              <XAxis dataKey="name" {...axis} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} labelFormatter={(l) => `${l} years`} />
              <Bar dataKey="count" fill={BRAND} radius={[2, 2, 0, 0]} barSize={44}>
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontFamily: FONT, fill: MUTED }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Salary expectation" icon={DollarSign}
              subtitle="USD per month, as typed. Grouped for reading; the underlying figures are exact.">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={dist} margin={{ left: -18, right: 8 }}>
              <XAxis dataKey="name" {...axis} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]} barSize={38}>
                {dist.map((b) => <Cell key={b.name} fill={b.color} />)}
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontFamily: FONT, fill: MUTED }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Expectation vs experience" icon={DollarSign}
              subtitle="One dot per candidate. The whole pool — nobody opted out of the question.">
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart margin={{ left: 4, right: 12, top: 8 }}>
              <CartesianGrid stroke={PAPER} />
              <XAxis type="number" dataKey="x" name="Years" {...axis} height={44}
                     label={{ value: "Years of experience", position: "insideBottom", offset: -4, style: { fontSize: 10, fill: MUTED, fontFamily: FONT } }} />
              <YAxis type="number" dataKey="y" name="USD/mo" {...axis} width={40} tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
              <Tooltip {...tip} cursor={{ strokeDasharray: "3 3" }} labelFormatter={() => ""}
                       formatter={(v, n) => (n === "USD/mo" ? [usd(v), "Expectation"] : [`${v} yr`, "Experience"])} />
              <Scatter data={scatter} fill={BRAND} fillOpacity={0.6} />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Certifications held" icon={Award} subtitle="Top eight across this pool.">
          {byCert.length === 0 ? (
            <p className="text-sm py-12 text-center" style={{ color: MUTED }}>No certifications declared in this pool.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(160, byCert.length * 30)}>
              <BarChart data={byCert} layout="vertical" margin={{ left: 0, right: 24 }}>
                <XAxis type="number" {...axis} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={80} {...axis} />
                <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} />
                <Bar dataKey="count" radius={[0, 2, 2, 0]} barSize={14}>
                  {byCert.map((_, i) => <Cell key={i} fill={BANDS[Math.min(i, 5)].color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Where candidates live" icon={MapPin} subtitle="Top eight countries of residence.">
          <ResponsiveContainer width="100%" height={Math.max(160, byCountry.length * 30)}>
            <BarChart data={byCountry} layout="vertical" margin={{ left: 0, right: 24 }}>
              <XAxis type="number" {...axis} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={110} {...axis} />
              <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} />
              <Bar dataKey="count" fill={BRAND} radius={[0, 2, 2, 0]} barSize={14}>
                <LabelList dataKey="count" position="right" style={{ fontSize: 10, fontFamily: FONT, fill: MUTED }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Travel availability" icon={Plane}
              subtitle="In order of commitment, not ranked by size. Check this against roles that need client-site presence.">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={byTravel} margin={{ left: -18, right: 8 }}>
              <XAxis dataKey="name" {...axis} />
              <YAxis {...axis} allowDecimals={false} />
              <Tooltip {...tip} formatter={(v) => [`${v} candidates`, ""]} labelFormatter={(l, p) => p?.[0]?.payload?.label || l} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]} barSize={44}>
                {byTravel.map((t) => <Cell key={t.name} fill={t.color} />)}
                <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontFamily: FONT, fill: MUTED }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {!fJob && byRoleSalary.length > 0 && (
          <Card title="Median expectation by role" icon={DollarSign}
                subtitle="Top 10 by volume. The whisker is the middle half of candidates — read it before you read the bar.">
            <ResponsiveContainer width="100%" height={Math.max(200, byRoleSalary.length * 32)}>
              <BarChart data={byRoleSalary} layout="vertical" margin={{ left: 0, right: 56 }}>
                <XAxis type="number" {...axis} tickFormatter={(v) => `${Math.round(v / 100) / 10}k`} />
                <YAxis type="category" dataKey="name" width={132} {...axis} />
                <Tooltip {...tip} labelFormatter={(l, p) => p?.[0]?.payload?.full || l}
                         formatter={(v, n, p) => [`${usd(v)} · ${p.payload.n} candidates` +
                                                  (p.payload.budget ? ` · budgeted ${p.payload.budget}` : ""), "Median"]} />
                <Bar dataKey="med" fill={INK} radius={[0, 2, 2, 0]} barSize={15}>
                  <ErrorBar dataKey="err" direction="x" width={4} strokeWidth={1} stroke={BRAND} />
                  <LabelList dataKey="med" position="right" formatter={usd}
                             style={{ fontSize: 10, fontFamily: FONT, fill: MUTED }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      <p className="text-xs mt-5" style={{ color: MUTED, lineHeight: 1.6 }}>
        The figure is required, so these charts cover all {rows.length} candidates rather than the ones who
        volunteered a number. The trade is on the other side: a required field makes people who had no
        figure in mind produce one anyway. This is what candidates ask for, which is not the same as what
        they would accept, and not the same as the market rate.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Admin panel
   ═══════════════════════════════════════════════════════════════════ */
function AdminPanel({ jobs, reloadJobs, me, exit, setToast, certs = [], reloadCerts }) {
  const [tab, setTab] = useState("apps");
  const [apps, setApps] = useState([]);
  const [sel, setSel] = useState(null);
  const [fJob, setFJob] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    try { setApps(await api.getApplications()); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Filtering stays client-side: the API caps at 500 rows, which is a
  // year of hiring at Linchpin's volume. Move it server-side when a
  // recruiter can feel the list.
  const rows = apps.filter((a) =>
    (!fJob || a.jobId === fJob) && (!fStatus || a.status === fStatus) &&
    (!q || `${a.firstName} ${a.lastName} ${a.email} ${a.jobTitle} ${a.reference}`.toLowerCase().includes(q.toLowerCase()))
  ).sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

  // Optimistic, then reconciled: a status dropdown that waits on a round
  // trip feels broken. If the write fails we put it back and say so.
  const setStatus = async (id, status) => {
    const before = apps;
    setApps(apps.map((a) => (a.id === id ? { ...a, status } : a)));
    if (sel?.id === id) setSel({ ...sel, status });
    try { await api.updateApplication(id, { status }); }
    catch (e) { setApps(before); setToast({ msg: `Couldn't save: ${e.message}` }); }
  };

  const setNote = async (id, note) => {
    try { await api.updateApplication(id, { internalNote: note }); }
    catch (e) { setToast({ msg: `Note didn't save: ${e.message}` }); }
  };

  const download = async (a) => {
    try { await api.downloadResume(a); }
    catch (e) { setToast({ msg: e.message }); }
  };

  const exportCsv = () => {
    const cols = ["id", "jobId", "jobTitle", "department", "firstName", "lastName", "email", "phone",
                  "country", "linkedin", "years", "certs", "salary", "notice", "authorized", "travel",
                  "source", "status", "submittedAt"];
    const val = (r, c) => (c === "country" ? country(r.country)?.name ?? ""
                        : c === "travel" ? travel(r.travel)?.label ?? ""
                        : Array.isArray(r[c]) ? r[c].join("; ") : r[c] ?? "");
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(val(r, c))).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `linchpin-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Erasure is by person, not by row: a deletion request means everything
  // we hold on them, across every role they applied to.
  const removeApp = async (a) => {
    if (!confirm(`Erase everything for ${a.firstName} ${a.lastName} (${a.email})?\n\n` +
                 `This deletes every application they've sent us and every resume, across all roles. ` +
                 `It cannot be undone.`)) return;
    try {
      const { resumesDeleted } = await api.eraseCandidate(a.email);
      setToast({ msg: `Erased. ${resumesDeleted} resume${resumesDeleted === 1 ? "" : "s"} deleted.` });
      setSel(null); reload();
    } catch (e) { setToast({ msg: e.message }); }
  };

  const counts = STATUSES.reduce((c, s) => ({ ...c, [s]: apps.filter((a) => a.status === s).length }), {});

  return (
    <div style={{ background: PAPER, minHeight: "80vh" }}>
      <div className="px-5 md:px-10 py-4 flex items-center justify-between" style={{ background: INK }}>
        <div className="flex items-center gap-3">
          <Shield size={15} style={{ color: BRAND }} />
          <span className="hidden sm:inline font-semibold text-sm" style={{ color: "#fff" }}>Recruiting</span>
          <div className="flex gap-1 sm:ml-3">
            {[["apps", "Applications"], ["analytics", "Analytics"], ["jobs", "Jobs"], ["catalog", "Catalog"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className="px-3 py-1.5 text-xs font-semibold"
                      style={{ color: tab === k ? "#fff" : "#8E8886", background: tab === k ? "#3A3A3A" : "transparent", borderRadius: 2 }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden md:inline text-xs" style={{ color: "#8E8886" }}>{me?.name}</span>
          <button onClick={exit} className="text-xs font-semibold" style={{ color: "#8E8886" }}>Exit</button>
          <a href={api.logoutUrl()} className="text-xs font-semibold" style={{ color: "#8E8886" }}>Sign out</a>
        </div>
      </div>

      {tab === "analytics" && <Analytics apps={apps} jobs={jobs} certs={certs} />}
      {tab === "catalog" && <CertCatalog certs={certs} reloadCerts={reloadCerts} setToast={setToast} />}

      {tab === "apps" && (
        <div className="px-5 md:px-10 py-8 max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-2 mb-6">
            {STATUSES.filter((s) => counts[s] > 0).map((s) => (
              <button key={s} onClick={() => setFStatus(fStatus === s ? "" : s)} className="px-3 py-2 text-xs font-semibold"
                      style={{ background: fStatus === s ? INK : "#fff", color: fStatus === s ? "#fff" : INK,
                               border: `1px solid ${fStatus === s ? INK : RULE}`, borderRadius: 2 }}>
                {s} <span style={{ color: fStatus === s ? "#B5AEAC" : MUTED }}>{counts[s]}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            <div className="relative flex-1 min-w-48">
              <Search size={15} style={{ position: "absolute", left: 12, top: 13, color: MUTED }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search candidates or reference" style={{ paddingLeft: 36 }} />
            </div>
            <Select value={fJob} onChange={(e) => setFJob(e.target.value)} style={{ width: "auto", minWidth: 180 }}>
              <option value="">All jobs</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </Select>
            <Btn variant="ghost" icon={Download} onClick={exportCsv} disabled={rows.length === 0}>CSV</Btn>
          </div>

          {loading ? (
            <div className="p-12 text-center"><Loader2 size={20} className="animate-spin" style={{ color: BRAND, margin: "0 auto" }} /></div>
          ) : err ? (
            <div className="p-12 text-center" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
              <p className="font-semibold mb-1" style={{ color: BRAND }}>{err}</p>
              <button onClick={reload} className="text-xs font-semibold mt-3" style={{ color: INK }}>Retry</button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
              <p className="font-semibold mb-1" style={{ color: INK }}>No applications here.</p>
              <p className="text-sm" style={{ color: MUTED }}>
                {apps.length === 0 ? "As candidates apply, they'll land in this queue." : "No results for these filters."}
              </p>
            </div>
          ) : (
            <div style={{ background: "#fff", border: `1px solid ${RULE}` }}>
              {rows.slice(0, 200).map((a) => (
                <div key={a.id} onClick={() => setSel(a)} className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50"
                     style={{ borderBottom: `1px solid ${RULE}` }}>
                  <div className="flex items-center justify-center font-semibold shrink-0"
                       style={{ width: 34, height: 34, background: PAPER, color: BRAND, fontSize: 12, borderRadius: 2 }}>
                    {a.firstName[0]}{a.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" style={{ color: INK }}>{a.firstName} {a.lastName}</div>
                    <div className="text-xs truncate mt-0.5" style={{ color: MUTED }}>
                      {a.jobTitle} · {a.years} yr · {usd(a.salary)} · {country(a.country)?.code}
                      {TRAVELS.includes(a.travel) && <span style={{ color: BRAND }}> · travels</span>}
                    </div>
                  </div>
                  <div className="hidden md:block text-xs shrink-0" style={{ color: MUTED }}>{fmtDate(a.submittedAt)}</div>
                  <StatusPill status={a.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "jobs" && <JobAdmin jobs={jobs} reloadJobs={reloadJobs} apps={apps} setToast={setToast} certs={certs} />}

      {sel && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,.45)" }} onClick={() => setSel(null)}>
          <div className="w-full max-w-lg overflow-y-auto" style={{ background: "#fff" }} onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between px-6 py-4" style={{ background: "#fff", borderBottom: `1px solid ${RULE}` }}>
              <span className="text-xs font-bold uppercase" style={{ color: MUTED, letterSpacing: "0.12em" }}>{sel.reference}</span>
              <button onClick={() => setSel(null)} style={{ color: MUTED }}><X size={18} /></button>
            </div>
            <div className="px-6 py-6">
              <h3 className="font-bold" style={{ color: INK, fontSize: 22, letterSpacing: "-0.01em" }}>{sel.firstName} {sel.lastName}</h3>
              <p className="text-sm mt-1 mb-5" style={{ color: BRAND }}>{sel.jobTitle} · {sel.jobId}</p>

              <Field label="Status">
                <Select value={sel.status} onChange={(e) => setStatus(sel.id, e.target.value)}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Btn variant="outline" icon={FileText} onClick={() => download(sel)} full>Download resume</Btn>

              {(sel.certs || []).length > 0 && (
                <div className="mt-7">
                  <h4 className="text-xs font-bold uppercase mb-3" style={{ color: INK, letterSpacing: "0.12em" }}>Certifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {sel.certs.map((c) => (
                      <span key={c} className="px-2.5 py-1.5 text-xs font-medium"
                            style={{ background: "#FDF6F5", color: BRAND, border: `1px solid ${RULE}`, borderRadius: 2 }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-7 space-y-3">
                {[
                  ["Email", sel.email], ["Phone", sel.phone],
                  ["Country of residence", country(sel.country)?.name], ["LinkedIn", sel.linkedin],
                  ["Experience", `${sel.years} years`], ["Notice", sel.notice],
                  ["Salary expectation", `${usd(sel.salary)} / month`],
                  ["Work authorization", sel.authorized],
                  ["Travel", travel(sel.travel)?.label], ["Source", sel.source],
                  ["Applied", fmtDate(sel.submittedAt)],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2" style={{ borderBottom: `1px solid ${PAPER}` }}>
                    <span className="text-xs font-semibold shrink-0" style={{ color: MUTED }}>{k}</span>
                    <span className="text-xs text-right break-all" style={{ color: INK }}>{v}</span>
                  </div>
                ))}
              </div>

              {sel.cover && (
                <div className="mt-7">
                  <h4 className="text-xs font-bold uppercase mb-3" style={{ color: INK, letterSpacing: "0.12em" }}>Why this role</h4>
                  <p className="text-sm p-4" style={{ color: "#4A4444", background: PAPER, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{sel.cover}</p>
                </div>
              )}

              <div className="mt-7">
                <Field label="Internal notes" hint="Only the recruiting team sees this.">
                  <Textarea defaultValue={sel.note || ""} onBlur={(e) => setNote(sel.id, e.target.value)}
                            placeholder="Screening notes, interview feedback…" style={{ minHeight: 90 }} />
                </Field>
              </div>

              <button onClick={() => removeApp(sel)} className="flex items-center gap-2 text-xs font-semibold mt-4 mb-8" style={{ color: BRAND }}>
                <Trash2 size={13} /> Delete application and resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Tag input: catalog suggestions, but anything can be typed. Boxing a
      recruiter into a fixed list of degrees makes them pick the nearest
      wrong one. ─────────────────────────────────────────────────────── */
function TagInput({ value = [], onChange, suggestions = [], placeholder }) {
  const [draft, setDraft] = useState("");
  const listId = useMemo(() => `dl${Math.random().toString(36).slice(2, 8)}`, []);
  const add = (raw) => {
    const v = raw.trim();
    if (!v || value.includes(v)) { setDraft(""); return; }
    onChange([...value, v]); setDraft("");
  };
  return (
    <div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium"
                  style={{ background: "#FDF6F5", color: BRAND, border: `1px solid ${RULE}`, borderRadius: 2 }}>
              {t}
              <button onClick={() => onChange(value.filter((x) => x !== t))} aria-label={`Remove ${t}`} style={{ color: BRAND }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} list={listId}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }} />
        <button onClick={() => add(draft)} disabled={!draft.trim()} className="px-3 text-xs font-semibold shrink-0"
                style={{ border: `1px solid ${RULE}`, color: draft.trim() ? BRAND : "#C7C2BF", borderRadius: 2 }}>
          Add
        </button>
      </div>
      <datalist id={listId}>
        {suggestions.filter((x) => !value.includes(x)).map((x) => <option key={x} value={x} />)}
      </datalist>
    </div>
  );
}

// options: [{ value, label }]; value/onChange work in `value` (codes).
const ChipPicker = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const on = value.includes(o.value);
      return (
        <button key={o.value} onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}
                className="px-3 py-2 text-xs font-medium text-left"
                style={{ border: `1px solid ${on ? BRAND : RULE}`, borderRadius: 2, background: on ? "#FDF6F5" : "#fff", color: on ? BRAND : INK }}>
          {on && <Check size={11} style={{ display: "inline", marginRight: 5, marginTop: -2 }} />}{o.label}
        </button>
      );
    })}
  </div>
);

const JobStatusPill = ({ status }) => {
  const st = jobStatus(status);
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold shrink-0"
          style={{ color: st.color, background: `${st.color}14`, borderRadius: 2 }}>
      <span style={{ width: 5, height: 5, borderRadius: 9, background: st.color }} />{st.label}
    </span>
  );
};

/* ── Job editor ──────────────────────────────────────────────────── */
const blankJob = () => ({
  id: "", title: "", department: DEPARTMENTS[0], location: CR, type: "Full time",
  remote: "Hybrid", band: "B3", status: "draft", summary: "",
  responsibilities: [], qualifications: [], nice: [], ...blankReqs(),
});

const linesToArr = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);

function JobEditor({ job, jobs, onSave, onDelete, onCancel, applicants, saving, certs = [] }) {
  const isNew = !job.id;
  const [f, setF] = useState({
    ...job,
    responsibilitiesText: (job.responsibilities || []).join("\n"),
    qualificationsText: (job.qualifications || []).join("\n"),
    niceText: (job.nice || []).join("\n"),
  });
  const [err, setErr] = useState({});
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const setV = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = () => {
    const e = {};
    if (!f.title.trim()) e.title = "Required.";
    if (!f.summary.trim()) e.summary = "Required — this is the paragraph candidates decide on.";
    if (f.yearsRequired === "" || isNaN(+f.yearsRequired) || +f.yearsRequired < 0 || +f.yearsRequired > 40)
      e.yearsRequired = "Enter a number between 0 and 40.";
    if (!isNew && f.status === "published" && !f.summary.trim()) e.summary = "A published role needs a summary.";
    setErr(e);
    if (Object.keys(e).length) return;

    const { responsibilitiesText, qualificationsText, niceText, ...rest } = f;
    onSave({
      ...rest,
      id: f.id || `JR${Math.floor(104000 + Math.random() * 900)}${Math.floor(Math.random() * 9)}`,
      yearsRequired: +f.yearsRequired,
      responsibilities: linesToArr(responsibilitiesText),
      qualifications: linesToArr(qualificationsText),
      nice: linesToArr(niceText),
      postedAt: f.postedAt || new Date().toISOString(),
    });
  };

  const H = ({ children }) => (
    <h4 className="text-xs font-bold uppercase mt-7 mb-4 pb-2"
        style={{ color: INK, letterSpacing: "0.12em", borderBottom: `1px solid ${RULE}` }}>{children}</h4>
  );

  return (
    <div className="px-5 md:px-10 py-8 max-w-3xl mx-auto">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-xs font-semibold mb-6" style={{ color: MUTED }}>
        <ChevronLeft size={14} /> All job postings
      </button>

      <div className="p-6 md:p-8" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="font-bold" style={{ color: INK, fontSize: 24, letterSpacing: "-0.01em" }}>
            {isNew ? "New job posting" : f.title || "Untitled role"}
          </h2>
          {!isNew && <JobStatusPill status={f.status} />}
        </div>
        <p className="text-sm mb-2" style={{ color: MUTED }}>
          {isNew ? "Nothing goes live until you set it to Published." : `${f.id} · ${applicants} applicant${applicants === 1 ? "" : "s"}`}
        </p>

        <H>Visibility</H>
        <Field label="Status" hint={jobStatus(f.status).hint}>
          <Select value={f.status} onChange={set("status")}>
            {JOB_STATUS.map((j) => <option key={j.code} value={j.code}>{j.label}</option>)}
          </Select>
        </Field>
        {f.status === "closed" && applicants > 0 && (
          <p className="text-xs -mt-2 mb-5 px-3 py-2" style={{ background: PAPER, color: MUTED, lineHeight: 1.5 }}>
            Closing hides the posting. The {applicants} application{applicants === 1 ? "" : "s"} already on it stay
            in the queue, and their candidates keep their reference number.
          </p>
        )}

        <H>The basics</H>
        <Field label="Job title" required error={err.title}><Input value={f.title} onChange={set("title")} /></Field>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Practice area">
            <Select value={f.department} onChange={set("department")}>
              {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
            </Select>
          </Field>
          <Field label="Location"><Input value={f.location} onChange={set("location")} /></Field>
          <Field label="Work setup">
            <Select value={f.remote} onChange={set("remote")}>
              <option>Hybrid</option><option>Remote</option><option>On site</option>
            </Select>
          </Field>
          <Field label="Employment type">
            <Select value={f.type} onChange={set("type")}>
              <option>Full time</option><option>Part time</option><option>Contract</option>
            </Select>
          </Field>
          <Field label="Budgeted band" hint="Internal. Never rendered on the public posting.">
            <Select value={f.band} onChange={set("band")}>
              {BANDS.map((b) => <option key={b.code} value={b.code}>{b.label} / month</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Summary" required error={err.summary}
               hint="Two or three sentences. This is the paragraph a candidate decides on.">
          <Textarea value={f.summary} onChange={set("summary")} style={{ minHeight: 90 }} />
        </Field>

        <H>Requirements</H>
        <p className="text-xs -mt-2 mb-5" style={{ color: MUTED, lineHeight: 1.6 }}>
          These render on the posting and are what you filter and report on later. Ask for what the
          job needs — every requirement you add narrows the pool, and the ones that aren't real
          narrow it in ways you won't see.
        </p>
        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Years of experience required" error={err.yearsRequired} hint="0 means open to early career.">
            <Input type="number" min="0" max="40" value={f.yearsRequired} onChange={set("yearsRequired")} />
          </Field>
          <Field label="Minimum education level">
            <Select value={f.edu} onChange={set("edu")}>
              {EDU_LEVELS.map((e) => <option key={e.code} value={e.code}>{e.label}</option>)}
            </Select>
          </Field>
        </div>

        {f.edu !== "NONE" && (
          <Field label="Acceptable fields of study" hint="Type and press Enter. Leave empty to accept any field.">
            <TagInput value={f.bachelorFields} onChange={(v) => setV("bachelorFields", v)}
                      suggestions={FIELDS} placeholder="e.g. Computer Science" />
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label="Master's degree">
            <Select value={f.masterReq} onChange={set("masterReq")}>
              {MASTER_REQ.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
            </Select>
          </Field>
        </div>
        {f.masterReq !== "NONE" && (
          <Field label="Acceptable master's fields" hint="Leave empty to accept any postgraduate field.">
            <TagInput value={f.masterFields} onChange={(v) => setV("masterFields", v)}
                      suggestions={FIELDS} placeholder="e.g. Data Science" />
          </Field>
        )}

        <Field label="Required certifications" hint="Hard requirement — a candidate without these is out.">
          <ChipPicker options={certs.map((c) => ({ value: c.code, label: c.name }))} value={f.certsRequired}
                      onChange={(v) => setV("certsRequired", v.filter((c) => !f.certsPreferred.includes(c)))} />
        </Field>
        <Field label="Preferred certifications" hint="Valued, not a gate.">
          <ChipPicker options={certs.filter((c) => !f.certsRequired.includes(c.code)).map((c) => ({ value: c.code, label: c.name }))}
                      value={f.certsPreferred} onChange={(v) => setV("certsPreferred", v)} />
        </Field>

        <H>The posting</H>
        <Field label="What you'll do" hint="One responsibility per line.">
          <Textarea value={f.responsibilitiesText} onChange={set("responsibilitiesText")} />
        </Field>
        <Field label="What we're looking for" hint="One per line. The prose version — the structured requirements above are what gets filtered.">
          <Textarea value={f.qualificationsText} onChange={set("qualificationsText")} />
        </Field>
        <Field label="Nice to have" hint="One per line.">
          <Textarea value={f.niceText} onChange={set("niceText")} style={{ minHeight: 80 }} />
        </Field>

        <div className="flex flex-wrap gap-3 mt-8 pt-6" style={{ borderTop: `1px solid ${RULE}` }}>
          <Btn onClick={save} disabled={saving} icon={saving ? Loader2 : undefined}>
            {saving ? "Saving…" : isNew ? "Create posting" : "Save changes"}
          </Btn>
          <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
          {!isNew && applicants === 0 && (
            <button onClick={() => onDelete(f)} className="flex items-center gap-2 text-xs font-semibold ml-auto" style={{ color: BRAND }}>
              <Trash2 size={13} /> Delete posting
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Job list ────────────────────────────────────────────────────── */
function JobAdmin({ jobs, reloadJobs, apps, setToast, certs = [] }) {
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  // The count comes from the API's own join, not from whatever slice of
  // applications this session happens to have loaded.
  const countFor = (id) => jobs.find((j) => j.id === id)?.applicants
    ?? apps.filter((a) => a.jobId === id).length;

  const save = async (job) => {
    setSaving(true);
    try {
      const saved = await api.saveJob(job);
      await reloadJobs();
      setToast({ msg: saved.status === "published" ? `"${saved.title}" is live on the site.` : "Saved as draft." });
      setEditing(null);
    } catch (e) { setToast({ msg: e.message }); }
    finally { setSaving(false); }
  };

  // The API refuses this with a 409 if anyone has applied; the confirm is a
  // courtesy, the FK is the rule.
  const remove = async (job) => {
    if (!confirm(`Delete "${job.title}"?`)) return;
    try {
      await api.deleteJob(job.id);
      await reloadJobs();
      setToast({ msg: "Posting deleted." });
      setEditing(null);
    } catch (e) { setToast({ msg: e.message }); }
  };

  if (editing) {
    return <JobEditor job={editing} jobs={jobs} applicants={countFor(editing.id)} saving={saving} certs={certs}
                      onSave={save} onDelete={remove} onCancel={() => setEditing(null)} />;
  }

  const rows = filter ? jobs.filter((j) => j.status === filter) : jobs;
  const counts = JOB_STATUS.reduce((c, st) => ({ ...c, [st.code]: jobs.filter((j) => j.status === st.code).length }), {});

  return (
    <div className="px-5 md:px-10 py-8 max-w-4xl mx-auto">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {JOB_STATUS.map((st) => (
            <button key={st.code} onClick={() => setFilter(filter === st.code ? "" : st.code)}
                    className="px-3 py-2 text-xs font-semibold"
                    style={{ background: filter === st.code ? INK : "#fff", color: filter === st.code ? "#fff" : INK,
                             border: `1px solid ${filter === st.code ? INK : RULE}`, borderRadius: 2 }}>
              {st.label} <span style={{ color: filter === st.code ? "#B5AEAC" : MUTED }}>{counts[st.code]}</span>
            </button>
          ))}
        </div>
        <Btn icon={Plus} onClick={() => setEditing(blankJob())}>New posting</Btn>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${RULE}` }}>
        {rows.length === 0 ? (
          <p className="p-12 text-center text-sm" style={{ color: MUTED }}>No postings with that status.</p>
        ) : rows.map((j) => (
          <div key={j.id} className="flex items-center gap-4 px-5 py-4" style={{ borderBottom: `1px solid ${RULE}` }}>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEditing(j)}>
              <div className="font-semibold text-sm truncate" style={{ color: j.status === "published" ? INK : "#A9A2A0" }}>
                {j.title}
              </div>
              <div className="text-xs mt-0.5 truncate" style={{ color: MUTED }}>
                {j.id} · {j.department} · {j.yearsRequired}+ yr · {j.band ? band(j.band).short : "—"} · {countFor(j.id)} applicants
              </div>
            </div>
            <JobStatusPill status={j.status} />
            <button onClick={() => setEditing(j)} className="px-3 py-1.5 text-xs font-semibold shrink-0"
                    style={{ border: `1px solid ${RULE}`, color: BRAND, borderRadius: 2 }}>Edit</button>
            <button onClick={() => remove(j)} className="shrink-0" style={{ color: countFor(j.id) > 0 ? "#D8D2CF" : MUTED }}
                    title={countFor(j.id) > 0 ? "Has applications — close it instead" : "Delete"}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Manage Certification Catalog ─────────────────────────────────────
   The catalog is the single source for the cert chips in the candidate form
   and the job editor. Codes are the permanent id (FK); the name is the label
   everyone sees. A cert in use by a posting or an application can't be deleted
   until it's removed there — the FK is RESTRICT and orphaning it would lie. */
function CertCatalog({ certs, reloadCerts, setToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState({ code: "", name: "", issuer: "", sortOrder: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try { setRows(await api.getCertCatalog()); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Refresh this list AND the app-wide catalog, so the editor/candidate form update.
  const refresh = async () => { await reload(); reloadCerts?.(); };

  const add = async () => {
    const body = {
      code: draft.code.trim(), name: draft.name.trim(),
      issuer: draft.issuer.trim() || "Microsoft",
      sortOrder: draft.sortOrder === "" ? undefined : Number(draft.sortOrder),
    };
    if (!body.code || !body.name) { setToast({ msg: "Code and name are required." }); return; }
    setBusy(true);
    try {
      await api.createCertification(body);
      setDraft({ code: "", name: "", issuer: "", sortOrder: "" });
      setToast({ msg: `Added ${body.code}.` });
      await refresh();
    } catch (e) { setToast({ msg: e.message }); }
    finally { setBusy(false); }
  };

  const saveRow = (r) => async (patch) => {
    try {
      await api.updateCertification(r.code, {
        name: patch.name ?? r.name, issuer: patch.issuer ?? r.issuer,
        sortOrder: patch.sortOrder ?? r.sort_order,
      });
      await refresh();
    } catch (e) { setToast({ msg: e.message }); reload(); }
  };

  const remove = async (r) => {
    if (r.jobs > 0 || r.applicants > 0) {
      setToast({ msg: `"${r.name}" is used by ${r.jobs} posting${r.jobs === 1 ? "" : "s"} and ${r.applicants} applicant${r.applicants === 1 ? "" : "s"}. Remove it there first.` });
      return;
    }
    if (!confirm(`Delete "${r.name}" (${r.code}) from the catalog?`)) return;
    try { await api.deleteCertification(r.code); setToast({ msg: "Deleted." }); await refresh(); }
    catch (e) { setToast({ msg: e.message }); }
  };

  return (
    <div className="px-5 md:px-10 py-8 max-w-4xl mx-auto">
      <h2 className="font-bold mb-1" style={{ color: INK, fontSize: 20, letterSpacing: "-0.01em" }}>Certification catalog</h2>
      <p className="text-sm mb-6" style={{ color: MUTED, maxWidth: 620 }}>
        The certifications recruiters can require or prefer on a posting, and that candidates pick from.
        The <b>code</b> is a permanent id and can't be changed; the <b>name</b> is the label everyone sees.
      </p>

      <div className="p-4 mb-6" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
        <div className="grid gap-2" style={{ gridTemplateColumns: "120px 1fr 130px 70px auto" }}>
          <Input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="Code (e.g. TOGAF)" />
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name shown in the UI" />
          <Input value={draft.issuer} onChange={(e) => setDraft({ ...draft, issuer: e.target.value })} placeholder="Issuer" />
          <Input type="number" value={draft.sortOrder} onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })} placeholder="Sort" />
          <Btn variant="solid" onClick={add} disabled={busy || !draft.code.trim() || !draft.name.trim()}>Add</Btn>
        </div>
        <p className="text-xs mt-2" style={{ color: MUTED }}>Code: letters, digits and <code>. _ / + -</code>. Lower sort shows first.</p>
      </div>

      {loading ? (
        <div className="p-12 text-center"><Loader2 size={20} className="animate-spin" style={{ color: BRAND, margin: "0 auto" }} /></div>
      ) : err ? (
        <div className="p-12 text-center" style={{ background: "#fff", border: `1px solid ${RULE}` }}>
          <p className="font-semibold mb-1" style={{ color: BRAND }}>{err}</p>
          <button onClick={reload} className="text-xs font-semibold mt-3" style={{ color: INK }}>Retry</button>
        </div>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${RULE}` }}>
          {rows.map((r) => (
            <div key={r.code} className="grid items-center gap-2 px-4 py-3"
                 style={{ gridTemplateColumns: "120px 1fr 130px 70px auto", borderBottom: `1px solid ${RULE}` }}>
              <span className="text-xs font-bold" style={{ color: BRAND }}>{r.code}</span>
              <Input defaultValue={r.name} onBlur={(e) => e.target.value.trim() && e.target.value !== r.name && saveRow(r)({ name: e.target.value.trim() })} />
              <Input defaultValue={r.issuer} onBlur={(e) => e.target.value !== r.issuer && saveRow(r)({ issuer: e.target.value.trim() })} />
              <Input type="number" defaultValue={r.sort_order} onBlur={(e) => Number(e.target.value) !== r.sort_order && saveRow(r)({ sortOrder: Number(e.target.value) })} />
              <div className="flex items-center gap-3 justify-end">
                <span className="text-xs whitespace-nowrap" style={{ color: (r.jobs || r.applicants) ? INK : "#C7C2BF" }}
                      title={`${r.jobs} job posting(s), ${r.applicants} applicant(s)`}>
                  {r.jobs + r.applicants > 0 ? `${r.jobs}·${r.applicants}` : "—"}
                </span>
                <button onClick={() => remove(r)} title="Delete" style={{ color: (r.jobs || r.applicants) ? "#C7C2BF" : BRAND }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-8 text-center text-sm" style={{ color: MUTED }}>No certifications yet. Add one above.</div>}
        </div>
      )}
    </div>
  );
}

/* Real URLs so a job can be shared (LinkedIn) and survive a refresh.
   The SWA navigationFallback serves index.html for these paths. */
const pathFor = (r) =>
  r?.name === "job" ? `/job/${encodeURIComponent(r.id)}`
  : r?.name === "apply" ? `/apply/${encodeURIComponent(r.id)}`
  : r?.name === "admin" ? "/recruiter"
  : "/";

function routeFromPath() {
  const p = window.location.pathname;
  let m;
  if ((m = p.match(/^\/job\/([^/]+)/))) return { name: "job", id: decodeURIComponent(m[1]) };
  if ((m = p.match(/^\/apply\/([^/]+)/))) return { name: "apply", id: decodeURIComponent(m[1]) };
  if (p.startsWith("/recruiter")) return { name: "admin" };
  return { name: "jobs" };
}

/* Shown when a shared link points to a posting that has since closed. */
function Missing({ go }) {
  return (
    <div className="flex items-center justify-center px-5" style={{ minHeight: "70vh", background: PAPER, fontFamily: FONT }}>
      <div className="max-w-md text-center">
        <AlertCircle size={24} style={{ color: BRAND, margin: "0 auto 14px" }} />
        <p className="font-semibold mb-2" style={{ color: INK }}>This role isn't open anymore.</p>
        <p className="text-sm mb-6" style={{ color: MUTED }}>The posting may have closed, or the link is out of date.</p>
        <Btn variant="outline" onClick={() => go({ name: "jobs" })}>See all open roles</Btn>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   App
   ═══════════════════════════════════════════════════════════════════ */
export default function App() {
  const [route, setRoute] = useState(routeFromPath);   // deep-link aware on first load
  const [jobs, setJobs] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [certs, setCerts] = useState([]);   // certification catalog, the single source for cert chips
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);   // null → bot protection off

  const loadCerts = useCallback(() => api.getCertifications().then(setCerts).catch(() => {}), []);

  const loadJobs = useCallback(async (asRecruiter) => {
    // Recruiters need drafts and closed roles; the public endpoint returns
    // published only, and that asymmetry is the point.
    const list = asRecruiter ? await api.getAllJobs() : await api.getJobs();
    setJobs(list);
    return list;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const principal = await api.getPrincipal();
        setMe(principal);
        loadCerts();   // reference data, non-blocking: the job board must not wait on it
        api.getConfig().then((c) => setTurnstileSiteKey(c.turnstileSiteKey)).catch(() => {});
        await loadJobs(principal?.isRecruiter);
      } catch (e) {
        setError(e.message || "The site couldn't load its open roles.");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadJobs, loadCerts]);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 3800); return () => clearTimeout(t); } }, [toast]);
  useEffect(() => { window.scrollTo(0, 0); }, [route]);

  // Browser/phone back gesture: the router is state-based, so we mirror each
  // navigation into the History API. Without this, "back" has no SPA entry to
  // return to and nothing happens. popstate restores the previous view.
  useEffect(() => {
    try { window.history.replaceState(routeFromPath(), ""); } catch { /* ignore */ }
    const onPop = () => setRoute(routeFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = (r) => {
    try { window.history.pushState(r, "", pathFor(r)); } catch { /* ignore */ }
    setRoute(r);
  };
  const job = (id) => jobs.find((j) => j.id === id);

  const submitApplication = async (payload, file) => {
    try {
      const reference = await api.submitApplication(payload, file);
      go({ name: "done", ref: reference, id: payload.jobId });
      return reference;
    } catch (e) {
      return { error: e.message };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: "100vh", background: PAPER, fontFamily: FONT }}>
        <Loader2 size={22} className="animate-spin" style={{ color: BRAND }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center px-5" style={{ minHeight: "100vh", background: PAPER, fontFamily: FONT }}>
        <div className="max-w-md text-center">
          <AlertCircle size={24} style={{ color: BRAND, margin: "0 auto 14px" }} />
          <p className="font-semibold mb-2" style={{ color: INK }}>{error}</p>
          <p className="text-sm mb-6" style={{ color: MUTED }}>
            This is on us, not on you. Try again in a moment.
          </p>
          <Btn variant="outline" onClick={() => window.location.reload()}>Reload</Btn>
        </div>
      </div>
    );
  }

  let body;
  if (route.name === "jobs") body = <JobsBoard jobs={jobs} go={go} />;
  else if (route.name === "job") body = job(route.id) ? <JobDetail job={job(route.id)} go={go} certs={certs} /> : <Missing go={go} />;
  else if (route.name === "apply") body = job(route.id) ? <ApplyForm job={job(route.id)} onSubmit={submitApplication} go={go} certs={certs} turnstileSiteKey={turnstileSiteKey} /> : <Missing go={go} />;
  else if (route.name === "done") body = <Confirmation ref_={route.ref} job={job(route.id)} go={go} />;
  else if (route.name === "admin") {
    body = me?.isRecruiter
      ? <AdminPanel jobs={jobs} reloadJobs={() => loadJobs(true)} me={me} setToast={setToast}
                    certs={certs} reloadCerts={loadCerts} exit={() => go({ name: "jobs" })} />
      : <AdminGate me={me} go={go} />;
  }

  const chrome = route.name !== "admin";


  return (
    <div style={{ fontFamily: FONT, background: "#fff", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: ${BRAND} !important; box-shadow: 0 0 0 2px ${BRAND}22; }
        button:focus-visible, [role="button"]:focus-visible { outline: 2px solid ${BRAND}; outline-offset: 2px; }
        .lp-line { stroke-dasharray: 260; stroke-dashoffset: 260; animation: lpDraw .9s ease forwards; }
        @keyframes lpDraw { to { stroke-dashoffset: 0; } }
        .lp-node text { transition: fill .2s ease; }
        .lp-node:hover text { fill: #fff; }
        @media (prefers-reduced-motion: reduce) {
          .lp-line { animation: none; stroke-dashoffset: 0; }
          * { animation-duration: .001ms !important; transition-duration: .001ms !important; }
        }
      `}</style>

      {chrome && <Header go={go} />}
      {body}

      {chrome && (
        <footer className="px-5 md:px-10 py-8" style={{ background: INK }}>
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span style={{ width: 8, height: 8, borderRadius: 8, background: BRAND, display: "inline-block" }} />
              <span className="font-bold text-sm" style={{ color: "#fff" }}>Linchpin People</span>
            </div>
            <p className="text-xs" style={{ color: "#8E8886" }}>Data &amp; AI · Microsoft Solution Partner · San José, Costa Rica</p>
          </div>
        </footer>
      )}

      {toast && (
        <div className="fixed left-1/2 bottom-6 z-50 flex items-center gap-2 px-5 py-3 text-sm font-semibold"
             style={{ transform: "translateX(-50%)", background: INK, color: "#fff", borderRadius: 2, maxWidth: "92vw" }}>
          <Check size={15} style={{ color: "#6EE7A8" }} /> {toast.msg}
        </div>
      )}
    </div>
  );
}
